import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription 
} from '../../components/ui/dialog';
import { 
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription 
} from '../../components/ui/drawer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { getErrorMessage } from '../../lib/utils';
import { 
  Play, Pause, LogOut, Search, QrCode, Package, Sun, Moon, CloudSun, 
  Clock, TrendingUp, CheckCircle2, ChevronRight, X, Calendar,
  Camera, AlertCircle, User, AlertTriangle, Snowflake, Zap, Upload,
  Volume2, VolumeX, RotateCcw, History, Bell, Eye, Calculator, RefreshCw,
  FileText, PhoneCall
} from 'lucide-react';
// html5-qrcode is now used inside QrScannerDialog directly
import AlertasNotificacao from '../../components/AlertasNotificacao';
import PausaFlutuante from '../../components/PausaFlutuante';
import { 
  QrScannerDialog, 
  OFCard, 
  OFList, 
  OFTabs, 
  CongeladasDrawer,
  CategoriaDestaqueSection,
  TarefasDrawer,
  ConcluirTarefaModal,
  LoteConsultaDrawer,
  PesquisaDrawer,
  SemOperacoesModal,
  LogoutConfirmDialog,
} from '../../components/operador';
import BottomTabBar from '../../components/operador/BottomTabBar';
import TaskDetailScreen from '../../components/operador/TaskDetailScreen';
import StartOperationScreen from '../../components/operador/StartOperationScreen';
import OperacoesScreen from '../../components/operador/OperacoesScreen';
import { Skeleton } from '../../components/ui/skeleton';import AlertasScreen from '../../components/operador/AlertasScreen';
import PerfilScreen from '../../components/operador/PerfilScreen';
import TimerScreen from '../../components/operador/TimerScreen';
import { APP_VERSION } from '../../version';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MOTIVOS_PAUSA_PONTUAL = [
  { id: 'almoco', label: 'Almoço', icon: '🍽️' },
  { id: 'wc', label: 'Casa de banho', icon: '🚻' },
  { id: 'material', label: 'Aguardando material', icon: '📦' },
  { id: 'tecnico', label: 'Problema técnico', icon: '🔧' },
  { id: 'outro', label: 'Outro motivo', icon: '💬' }
];

export default function OperadorHome({ abrirTarefasPontuais = false }) {
  const { user: authUser, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { username: routeUsername } = useParams(); // Para rota /sim/operador/:username
  
  // Suporte para modo de simulação - via query param OU via rota
  const simUser = routeUsername || searchParams.get('sim_user');
  const [simulatedUser, setSimulatedUser] = useState(null);
  const user = simulatedUser || authUser;
  const isSimulation = !!simUser;
  
  // Detectar se está dentro de iframe
  const isInIframe = window.self !== window.top;
  
  // Função de navegação que funciona dentro de iframes
  const safeNavigate = (path) => {
    if (isInIframe) {
      window.location.href = path;
    } else {
      navigate(path);
    }
  };
  
  const [ofsData, setOfsData] = useState({ novas: [], iniciadas: [], terminadas: [] });
  const [operacoes, setOperacoes] = useState([]);
  const [producaoAtiva, setProducaoAtiva] = useState(null);
  const [emPausa, setEmPausa] = useState(false);
  const [bannerTempo, setBannerTempo] = useState(0); // segundos decorridos na produção ativa
  const [pausaProgramadaAtiva, setPausaProgramadaAtiva] = useState(false); // pausa programada activa (afeta tarefas pontuais)
  
  // Estado para som de alertas
  const [somAtivado, setSomAtivado] = useState(() => {
    const saved = localStorage.getItem('somAlertasAtivado');
    return saved !== null ? JSON.parse(saved) : true;
  });
  
  // Estado para modal de confirmação de logout
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [tempoPausaAtual, setTempoPausaAtual] = useState(0);
  const [motivoPausa, setMotivoPausa] = useState('');
  const [pausaHoraFim, setPausaHoraFim] = useState(''); // Hora prevista de fim da pausa
  const [showSearchDrawer, setShowSearchDrawer] = useState(false);
  const [showQrDialog, setShowQrDialog] = useState(false);
  // TaskDetail
  const [selectedOFDetail, setSelectedOFDetail] = useState(null);
  const [preSelectedLoteId, setPreSelectedLoteId] = useState(null);
  // StartOperation — substituir IniciarDialog
  const [selectedOpStart, setSelectedOpStart] = useState(null); // {of, op, lote}
  // Bottom nav tab
  const [bottomTab, setBottomTab] = useState('inicio');
  const [selectedOperacao, setSelectedOperacao] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ pecasHoje: 0, eficienciaHoje: 0, tarefasHoje: 0 });
  const [qrError, setQrError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [manualQrInput, setManualQrInput] = useState('');
  
  // Estado para drawer de consulta de lote completo
  const [showLoteConsulta, setShowLoteConsulta] = useState(false);
  const [loteConsultaData, setLoteConsultaData] = useState(null);
  const [loadingConsulta, setLoadingConsulta] = useState(false);  const [cameraPermission, setCameraPermission] = useState('prompt'); // 'prompt', 'granted', 'denied'
  const [scannerReady, setScannerReady] = useState(false);
  const [modoTestes, setModoTestes] = useState(false); // Modo testes - esconder tempos
  const [isIOS, setIsIOS] = useState(false); // Detectar iOS para instruções específicas
  const [showImageUpload, setShowImageUpload] = useState(false); // Fallback para upload de imagem
  
  // Estado para tabs
  const [activeTab, setActiveTab] = useState('novas'); // 'novas', 'iniciadas', 'terminadas'
  
  // Estado para categorias em destaque e operações congeladas
  const [categoriasDestaque, setCategoriasDestaque] = useState([]);
  const [operacoesCongeladas, setOperacoesCongeladas] = useState([]);
  const [showCongeladasDrawer, setShowCongeladasDrawer] = useState(false);
  const [filtroCategoria, setFiltroCategoria] = useState(null); // Filtro por categoria em destaque
  const [showSemOperacoesModal, setShowSemOperacoesModal] = useState(false);
  const [loteNaoAtribuido, setLoteNaoAtribuido] = useState(null); // Info do lote que foi lido mas não tem operações
  const [alertasNaoLidosCount, setAlertasNaoLidosCount] = useState(0); // Contagem de alertas não lidos
  const [avatarError, setAvatarError] = useState(false);
  // Reset avatarError quando o avatar muda (ex: nova foto, re-fetch)
  const avatarUrl = user?.avatar;
  useEffect(() => { setAvatarError(false); }, [avatarUrl]);
  
  // Estado para Bolsa de Tarefas
  const [tarefasAtribuidas, setTarefasAtribuidas] = useState([]);
  const [tarefasHistoricoDia, setTarefasHistoricoDia] = useState([]);
  const [showTarefasDrawer, setShowTarefasDrawer] = useState(false);
  const [tarefasTab, setTarefasTab] = useState('disponiveis'); // 'disponiveis' ou 'historico'
  const [selectedTarefa, setSelectedTarefa] = useState(null);
  const [showConcluirTarefaModal, setShowConcluirTarefaModal] = useState(false);
  const [tarefaConclusao, setTarefaConclusao] = useState({ quantidade: '' });
  const [operacoesInitialTab, setOperacoesInitialTab] = useState(null);
  
  // Estado para timer de tarefa pontual
  const [tarefaPontualTimer, setTarefaPontualTimer] = useState(null); // tarefa em execução
  const [tarefaPontualTempo, setTarefaPontualTempo] = useState(0);
  const [tarefaPontualPaused, setTarefaPontualPaused] = useState(false);
  const [showPausaPontualDrawer, setShowPausaPontualDrawer] = useState(false);
  const [motivoPausaPontual, setMotivoPausaPontual] = useState('');
  const [tarefaPontualFrozen, setTarefaPontualFrozen] = useState(false);
  const [showCongelarConfirm, setShowCongelarConfirm] = useState(false);
  const [showDescongelarConfirm, setShowDescongelarConfirm] = useState(null); // tarefa a descongelar
  const tarefaTimerRef = React.useRef(null);
  
  // Abrir aba Pontuais automaticamente quando abrirTarefasPontuais é true (via QR Code) ou ?tab=pontuais
  const tabParam = searchParams.get('tab');
  useEffect(() => {
    if ((abrirTarefasPontuais || tabParam === 'pontuais') && tarefasAtribuidas.length >= 0) {
      const timer = setTimeout(() => {
        setOperacoesInitialTab('pontuais');
        setBottomTab('operacoes');
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [abrirTarefasPontuais, tabParam, tarefasAtribuidas]);
  
  // Estado para pausas programadas
  const [pausasLegais, setPausasLegais] = useState([]);
  const [pausaNotificadaHoje, setPausaNotificadaHoje] = useState(null); // Para evitar notificações repetidas
  
  // Estado para verificação de versão
  const [versionStatus, setVersionStatus] = useState('checking'); // 'checking', 'ok', 'outdated'
  const [serverVersion, setServerVersion] = useState(null);

  // Verificar versão da aplicação
  useEffect(() => {
    const checkVersion = async () => {
      try {
        const response = await axios.get(`${API}/version`);
        const srvVersion = response.data.version;
        setServerVersion(srvVersion);
        
        if (srvVersion === APP_VERSION) {
          setVersionStatus('ok');
        } else {
          setVersionStatus('outdated');
          if (process.env.NODE_ENV === 'development') console.log(`[Versão] App: ${APP_VERSION}, Servidor: ${srvVersion} - DESATUALIZADA`);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.log('[Versão] Erro ao verificar versão:', error);
        setVersionStatus('ok'); // Em caso de erro, assumir que está ok
      }
    };
    
    checkVersion();
    // Verificar a cada 5 minutos
    const interval = setInterval(checkVersion, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Função para forçar atualização
  const forceRefresh = () => {
    // Limpar cache do service worker e recarregar
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          registration.unregister();
        });
      });
    }
    // Limpar caches
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }
    // Forçar reload completo
    window.location.reload(true);
  };

  // Carregar configuração para verificar modo testes
  useEffect(() => {
    loadConfiguracao();
    // Recarregar config a cada 5 min para apanhar pausas novas
    const interval = setInterval(loadConfiguracao, 300000);
    // Detectar iOS para instruções específicas de câmara
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isIOSDevice);
    if (process.env.NODE_ENV === 'development') console.log('[QR Scanner] Dispositivo iOS detectado:', isIOSDevice);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConfiguracao = async () => {
    try {
      const response = await axios.get(`${API}/configuracao`);
      setModoTestes(response.data.modo_testes_esconder_tempos || false);
      
      // Carregar pausas programadas
      const pausas = [];
      // Adicionar intervalo de almoço como pausa
      if (response.data.intervalo_almoco_inicio && response.data.intervalo_almoco_fim) {
        pausas.push({
          id: 'almoco',
          label: 'Almoço',
          inicio: response.data.intervalo_almoco_inicio,
          fim: response.data.intervalo_almoco_fim
        });
      }
      // Adicionar pausas adicionais
      if (response.data.pausas_adicionais) {
        response.data.pausas_adicionais.forEach((pausa, index) => {
          pausas.push({
            id: `pausa_${index}`,
            label: pausa.descricao || `Pausa ${index + 1}`,
            inicio: pausa.inicio,
            fim: pausa.fim
          });
        });
      }
      setPausasLegais(pausas);
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    }
  };

  // Timer do banner — calcula tempo decorrido (descontando pausas)
  useEffect(() => {
    if (!producaoAtiva?.registo?.inicio) { setBannerTempo(0); return; }
    const calcTempo = () => {
      const inicio = new Date(producaoAtiva.registo.inicio).getTime();
      const agora = Date.now();
      // Calcular duração total de pausas
      let pausaTotal = 0;
      const pausas = producaoAtiva.registo?.pausas || [];
      pausas.forEach(p => {
        const pInicio = new Date(p.inicio).getTime();
        const pFim = p.fim ? new Date(p.fim).getTime() : agora;
        pausaTotal += (pFim - pInicio);
      });
      const tempoLiquido = Math.max(0, Math.floor((agora - inicio - pausaTotal) / 1000));
      setBannerTempo(tempoLiquido);
    };
    calcTempo();
    // Se em pausa, não atualizar (valor estático)
    if (emPausa) return;
    const interval = setInterval(calcTempo, 1000);
    return () => clearInterval(interval);
  }, [producaoAtiva, emPausa]);

  
  // Pausas programadas — calcula timeouts exactos para início/fim de cada pausa
  useEffect(() => {
    if (!pausasLegais.length) return;
    
    const timers = [];
    
    const avaliar = () => {
      const agora = new Date();
      const horaAtual = `${agora.getHours().toString().padStart(2, '0')}:${agora.getMinutes().toString().padStart(2, '0')}`;
      const hoje = agora.toDateString();
      
      let emPausaAgora = false;
      let proximoEvento = Infinity; // ms até o próximo início ou fim de pausa
      
      for (const pausa of pausasLegais) {
        if (!pausa.inicio || !pausa.fim) continue;
        
        // Converter HH:MM em ms desde meia-noite
        const [hI, mI] = pausa.inicio.split(':').map(Number);
        const [hF, mF] = pausa.fim.split(':').map(Number);
        const inicioMs = (hI * 60 + mI) * 60000;
        const fimMs = (hF * 60 + mF) * 60000;
        const agoraMs = (agora.getHours() * 60 + agora.getMinutes()) * 60000 + agora.getSeconds() * 1000;
        
        if (horaAtual >= pausa.inicio && horaAtual < pausa.fim) {
          emPausaAgora = true;
          // Próximo evento: fim desta pausa
          const msAteFim = fimMs - agoraMs;
          if (msAteFim > 0 && msAteFim < proximoEvento) proximoEvento = msAteFim;
          
          const pausaKey = `${pausa.id}_${hoje}`;
          if (pausaNotificadaHoje !== pausaKey) {
            setPausaNotificadaHoje(pausaKey);
            toast.info(`Hora de ${pausa.label}`, {
              duration: 10000,
              description: `Até às ${pausa.fim}`
            });
          }
          break;
        } else if (horaAtual < pausa.inicio) {
          // Próximo evento: início desta pausa
          const msAteInicio = inicioMs - agoraMs;
          if (msAteInicio > 0 && msAteInicio < proximoEvento) proximoEvento = msAteInicio;
        }
      }
      
      setPausaProgramadaAtiva(emPausaAgora);
      
      // Agendar próxima verificação no momento exacto do próximo evento (+500ms de margem)
      if (proximoEvento < Infinity && proximoEvento > 0) {
        const t = setTimeout(avaliar, proximoEvento + 500);
        timers.push(t);
      }
    };
    
    avaliar();
    
    return () => timers.forEach(t => clearTimeout(t));
  }, [pausasLegais, pausaNotificadaHoje]);

  // Carregar utilizador simulado
  useEffect(() => {
    if (simUser) {
      loadSimulatedUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simUser]);

  const loadSimulatedUser = async () => {
    try {
      if (process.env.NODE_ENV === 'development') console.log('Loading simulated user:', simUser);
      const response = await axios.get(`${API}/operadores`);
      if (process.env.NODE_ENV === 'development') console.log('Operadores loaded:', response.data?.length);
      const operador = response.data.find(op => op.username === simUser) 
        || response.data.find(op => simUser.startsWith(op.username));
      if (process.env.NODE_ENV === 'development') console.log('Found operador:', operador);
      if (operador) {
        setSimulatedUser({
          id: operador.id,
          nome: operador.nome || operador.nome_completo || operador.username,
          nome_completo: operador.nome_completo,
          username: operador.username,
          perfil: 'operador',
          avatar: operador.foto_url ? `${BACKEND_URL}${operador.foto_url}` : null,
          foto_url: operador.foto_url
        });
      } else {
        console.error('Operador não encontrado:', simUser);
        setSimulatedUser({
          id: simUser,
          nome: simUser,
          username: simUser,
          perfil: 'operador'
        });
      }
    } catch (error) {
      console.error('Erro ao carregar utilizador simulado:', error);
      setSimulatedUser({
        id: simUser,
        nome: simUser,
        username: simUser,
        perfil: 'operador'
      });
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return { text: 'Bom dia', icon: Sun, color: '#F59E0B' };
    if (hour < 19) return { text: 'Boa tarde', icon: CloudSun, color: '#F97316' };
    return { text: 'Boa noite', icon: Moon, color: '#6366F1' };
  };

  const greeting = getGreeting();
  const GreetingIcon = greeting.icon;
  const greetingColor = greeting.color;

  const handleChamarGestor = async () => {
    try {
      const payload = {
        operador_id: user?.id,
        operador_nome: user?.nome_completo || user?.nome || user?.username,
      };
      // Incluir info de produção ativa se existir
      if (producaoAtiva) {
        payload.of_id = producaoAtiva.of?.id;
        payload.of_numero = producaoAtiva.of?.numero_of;
        payload.lote_id = producaoAtiva.lote?.id || producaoAtiva.registo?.lote_id;
        payload.lote_codigo = producaoAtiva.lote?.codigo_lote || producaoAtiva.registo?.lote_codigo;
        payload.operacao_id = producaoAtiva.registo?.operacao_id;
        payload.operacao_nome = producaoAtiva.operacao?.nome;
        payload.operacao_codigo = producaoAtiva.operacao?.codigo;
        payload.marca = producaoAtiva.of?.cliente;
        payload.modelo = producaoAtiva.of?.artigo;
      }
      await axios.post(`${API}/alertas-gestor/chamar`, payload);
      toast.success('Gestor notificado! Alguém virá em breve.', { duration: 5000 });
    } catch (error) {
      console.error('Erro ao chamar gestor:', error.message);
      toast.error('Erro ao enviar pedido ao gestor');
    }
  };

  useEffect(() => {
    // Em modo simulação, só carregar dados quando simulatedUser estiver disponível
    if (isSimulation && !simulatedUser) return;
    if (user?.id) {
      loadData();
      loadStats();
      // Restaurar tarefa pontual ativa (sobrevive a refresh do browser)
      restaurarTarefaPontualAtiva();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isSimulation, simulatedUser]);

  // Auto-refresh silencioso de operações e tarefas (a cada 15s)
  useEffect(() => {
    if (isSimulation && !simulatedUser) return;
    if (!user?.id) return;
    const interval = setInterval(async () => {
      try {
        const [ofsRes, congeladasRes, tarefasRes] = await Promise.all([
          axios.get(`${API}/operadores/${user.id}/ofs`),
          axios.get(`${API}/producao/congeladas/${user.id}`),
          axios.get(`${API}/tarefas/operador/${user.id}`).catch(() => ({ data: [] })),
        ]);
        setOfsData(ofsRes.data);
        setOperacoesCongeladas(congeladasRes.data || []);
        setTarefasAtribuidas(tarefasRes.data || []);
      } catch (e) {
        console.error('Erro ao atualizar dados:', e.message);
      }
    }, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isSimulation, simulatedUser]);

  // Polling: detectar quando o gestor encerra a operação ativa (a cada 3s + visibility)
  useEffect(() => {
    if (!user?.id || !producaoAtiva) return;
    let cancelled = false;

    const onEncerrada = async () => {
      // Operação foi encerrada externamente (pelo gestor)
      setProducaoAtiva(null);
      setEmPausa(false);
      setTempoPausaAtual(0);
      setBannerTempo(0);
      toast.info('A operação foi encerrada pelo gestor. A carregar próximos trabalhos...');
      try {
        const [ofsRes, congeladasRes] = await Promise.all([
          axios.get(`${API}/operadores/${user.id}/ofs`),
          axios.get(`${API}/producao/congeladas/${user.id}`),
        ]);
        setOfsData(ofsRes.data);
        setOperacoesCongeladas(congeladasRes.data || []);
      } catch (e) { if (process.env.NODE_ENV === 'development') console.warn('Polling OFs failed:', e); }
    };

    const checkAtiva = async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/producao/ativa/${user.id}`);
        if (cancelled) return;
        if (!res.data || !res.data.registo) await onEncerrada();
      } catch (e) { if (process.env.NODE_ENV === 'development') console.warn('Polling producao failed:', e); }
    };

    const checkInterval = setInterval(checkAtiva, 3000);
    const onVisible = () => { if (document.visibilityState === 'visible') checkAtiva(); };
    const onFocus = () => checkAtiva();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      clearInterval(checkInterval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, producaoAtiva]);

  const restaurarTarefaPontualAtiva = async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API}/tarefas/ativa/${user.id}`);
      if (res.data && res.data.id) {
        setTarefaPontualTimer(res.data);
        setTarefaPontualTempo(res.data.tempo_decorrido_segundos || 0);
        // Respeita estado congelado vindo do backend (sobrevive a logout / reload)
        const isCongelada = !!res.data.congelado || !!res.data.congelado_em;
        // Congelar não é o mesmo que Pausar — não ativar tarefaPontualPaused para tarefas congeladas
        setTarefaPontualPaused(false);
        setTarefaPontualFrozen(isCongelada);
        // NÃO abrir automaticamente o timer — apenas restaurar o estado
        // O banner flutuante irá informar o operador que há uma tarefa em curso
      }
    } catch (e) {
      // Sem tarefa ativa — ignorar
      if (process.env.NODE_ENV === 'development') console.debug('No active tarefa pontual:', e?.message || e);
    }
  };


  const loadData = async () => {
    if (!user?.id) return;
    try {
      // ─── ARRANQUE RÁPIDO ───
      // Antes: 6+ awaits sequenciais → em Wi-Fi de fábrica, arranque de 5-15s.
      // Agora: 1 Promise.all + versão "light" das OFs para primeiro paint rápido.
      // Depois: pedido "full" em background para preencher quantidades/anomalias.
      const [
        producaoRes,
        ofsRes,
        operacoesRes,
        categoriasRes,
        congeladasRes,
        tarefasRes,
        historicoRes,
      ] = await Promise.all([
        axios.get(`${API}/producao/ativa/${user.id}`).catch(() => ({ data: null })),
        axios.get(`${API}/operadores/${user.id}/ofs?light=true`).catch(() => ({ data: { novas: [], iniciadas: [], terminadas: [] } })),
        axios.get(`${API}/operacoes`).catch(() => ({ data: [] })),
        axios.get(`${API}/categorias`).catch(() => ({ data: [] })),
        axios.get(`${API}/producao/congeladas/${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/tarefas/operador/${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/tarefas/operador/${user.id}/historico-dia`).catch(() => ({ data: [] })),
      ]);

      // Produção ativa + estado de pausa
      if (producaoRes.data) {
        setProducaoAtiva(producaoRes.data);
        const pausas = producaoRes.data.registo?.pausas || [];
        if (pausas.length > 0 && !pausas[pausas.length - 1].fim) {
          setEmPausa(true);
          const pausaAtual = pausas[pausas.length - 1];
          setMotivoPausa(pausaAtual.motivo || '');
          setPausaHoraFim(pausaAtual.hora_fim || pausaAtual.horaFim || '');
          if (pausaAtual.inicio) {
            const inicioPausa = new Date(pausaAtual.inicio);
            const agora = new Date();
            const tempoPausaCalc = Math.floor((agora - inicioPausa) / 1000);
            setTempoPausaAtual(tempoPausaCalc);
          }
        } else {
          setEmPausa(false);
          setTempoPausaAtual(0);
          setMotivoPausa('');
        }
      } else {
        setEmPausa(false);
        setTempoPausaAtual(0);
        setMotivoPausa('');
      }

      // OFs (versão light: primeiro paint rápido)
      setOfsData(ofsRes.data || { novas: [], iniciadas: [], terminadas: [] });

      // Operações + categorias em destaque
      setOperacoes(operacoesRes.data || []);
      const destaque = (categoriasRes.data || []).filter((c) => c.destaque_app);
      setCategoriasDestaque(destaque);

      // Operações congeladas + tarefas + histórico
      setOperacoesCongeladas(congeladasRes.data || []);
      setTarefasAtribuidas(tarefasRes.data || []);
      setTarefasHistoricoDia(historicoRes.data || []);

      // ─── UPGRADE EM BACKGROUND ───
      // Faz o pedido "full" para preencher quantidade_produzida_todos, anomalias e "outros a trabalhar".
      // Sem bloquear o first paint — o utilizador já pode ver a lista de OFs.
      axios
        .get(`${API}/operadores/${user.id}/ofs`)
        .then((r) => setOfsData(r.data))
        .catch((e) => {
          if (process.env.NODE_ENV === 'development') console.debug('OFs full falhou:', e?.message);
        });
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  };

  const loadStats = async () => {
    if (!user?.id) return;
    try {
      const response = await axios.get(`${API}/analytics/operador/${user.id}?periodo=dia`);
      if (response.data) {
        setStats({
          pecasHoje: response.data.pecas_produzidas || 0,
          eficienciaHoje: response.data.eficiencia_media || 0,
          tarefasHoje: response.data.registos_count || 0
        });
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  // Funções para Bolsa de Tarefas
  const iniciarTarefaPontual = async (tarefa) => {
    try {
      // Persistir início no backend
      const res = await axios.post(`${API}/tarefas/${tarefa.id}/iniciar`, {
        operador_id: user?.id
      });
      const tarefaAtualizada = res.data || tarefa;
      setTarefaPontualTimer(tarefaAtualizada);
      
      // Calcular tempo decorrido se já existia início
      if (tarefaAtualizada.tempo_decorrido_segundos) {
        setTarefaPontualTempo(tarefaAtualizada.tempo_decorrido_segundos);
      } else {
        setTarefaPontualTempo(0);
      }
      setTarefaPontualPaused(false);
      setBottomTab('tarefa_timer');
    } catch (e) {
      console.error('Erro ao iniciar tarefa:', e);
      // Fallback: iniciar localmente
      setTarefaPontualTimer(tarefa);
      setTarefaPontualTempo(0);
      setTarefaPontualPaused(false);
      setBottomTab('tarefa_timer');
    }
  };

  // Timer effect para tarefa pontual — usa hora real do início + tempo_acumulado para evitar drift
  // Sobrevive a hibernação porque recalcula a cada tick com base no relógio do sistema.
  React.useEffect(() => {
    if (!tarefaPontualTimer || tarefaPontualPaused || tarefaPontualFrozen || pausaProgramadaAtiva) {
      if (tarefaTimerRef.current) clearInterval(tarefaTimerRef.current);
      return;
    }
    
    // Fonte de verdade: ultimo_descongelado_em (ou iniciado_em se nunca descongelada) + tempo_acumulado_segundos
    const inicioRef = tarefaPontualTimer.ultimo_descongelado_em || tarefaPontualTimer.iniciado_em;
    const acumuladoBase = Number(tarefaPontualTimer.tempo_acumulado_segundos || 0);
    
    const calcTempo = () => {
      if (inicioRef) {
        const inicio = new Date(inicioRef);
        const agora = new Date();
        const diffSeg = Math.max(0, Math.floor((agora - inicio) / 1000));
        setTarefaPontualTempo(acumuladoBase + diffSeg);
      } else {
        setTarefaPontualTempo(prev => prev + 1);
      }
    };
    
    calcTempo(); // Calcular imediatamente
    tarefaTimerRef.current = setInterval(calcTempo, 1000);
    
    return () => {
      if (tarefaTimerRef.current) clearInterval(tarefaTimerRef.current);
    };
  }, [tarefaPontualTimer, tarefaPontualPaused, tarefaPontualFrozen, pausaProgramadaAtiva]);

  const abrirTarefa = (tarefa) => {
    // Impedir iniciar tarefa pontual se já há uma produção ativa (operação de OF)
    if (producaoAtiva && !tarefaPontualTimer) {
      toast.error('Já tem uma operação em curso. Conclua ou congele a operação antes de iniciar uma tarefa pontual.');
      return;
    }
    // Se a tarefa está congelada, mostrar confirmação de retoma
    if (tarefaPontualFrozen && tarefaPontualTimer?.id === tarefa.id) {
      setShowDescongelarConfirm(tarefa);
      return;
    }
    // Abrir timer normalmente
    iniciarTarefaPontual(tarefa);
  };

  const concluirTarefaPontualTimer = () => {
    // Parar o timer e abrir o modal de conclusão
    if (tarefaTimerRef.current) clearInterval(tarefaTimerRef.current);
    setSelectedTarefa(tarefaPontualTimer);
    setTarefaConclusao({ quantidade: '' });
    setShowConcluirTarefaModal(true);
  };

  const concluirTarefa = async () => {
    if (!selectedTarefa) return;
    
    const quantidade = parseInt(tarefaConclusao.quantidade) || 0;
    
    // Se obriga contagem, quantidade é obrigatória
    if (selectedTarefa.obriga_contagem && (!quantidade || quantidade <= 0)) {
      toast.error('Esta tarefa obriga contagem. Indique a quantidade produzida.');
      return;
    }
    
    try {
      await axios.post(`${API}/tarefas/${selectedTarefa.id}/concluir`, {
        quantidade: quantidade
      });
      
      toast.success('Tarefa concluída com sucesso!');
      setShowConcluirTarefaModal(false);
      setSelectedTarefa(null);
      // Limpar estado do timer pontual
      setTarefaPontualTimer(null);
      setTarefaPontualTempo(0);
      setTarefaPontualPaused(false);
      setTarefaPontualFrozen(false);
      setBottomTab('inicio'); // Voltar à Home após concluir tarefa
      
      // Recarregar tarefas disponíveis e histórico
      const [tarefasRes, historicoRes] = await Promise.all([
        axios.get(`${API}/tarefas/operador/${user.id}`),
        axios.get(`${API}/tarefas/operador/${user.id}/historico-dia`)
      ]);
      setTarefasAtribuidas(tarefasRes.data || []);
      setTarefasHistoricoDia(historicoRes.data || []);
    } catch (error) {
      toast.error('Erro ao concluir tarefa');
      console.error(error);
    }
  };

  // Carregar histórico de tarefas do dia
  const carregarHistoricoTarefas = async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API}/tarefas/operador/${user.id}/historico-dia`);
      setTarefasHistoricoDia(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar histórico de tarefas:', error);
    }
  };

  // Polling de alertas não lidos para badge
  React.useEffect(() => {
    if (!user?.id) return;
    const fetchAlertasCount = async () => {
      try {
        const res = await axios.get(`${API}/configuracoes/alertas/activos`, {
          params: { usuario_id: user.id, perfil: 'operador' }
        });
        setAlertasNaoLidosCount((res.data?.nao_lidos || []).filter(a => !a.visto).length);
      } catch (e) { console.error('Erro ao buscar alertas:', e.message); }
    };
    fetchAlertasCount();
    const interval = setInterval(fetchAlertasCount, 15000);
    return () => clearInterval(interval);
  }, [user?.id]);

  // Função para consultar detalhes de um lote completo
  const consultarLoteCompleto = async (lote, of) => {
    setLoadingConsulta(true);
    setShowLoteConsulta(true);
    
    try {
      // Buscar registos de tempo deste lote para este operador
      const response = await axios.get(`${API}/registos-tempo/lote/${lote.id}`, {
        params: { operador_id: user.id }
      });
      
      const registos = response.data || [];
      
      // Buscar nomes das operações
      const operacoesMap = {};
      (of.operacoes || []).forEach(op => {
        operacoesMap[op.operacao_id] = op;
      });
      
      // Enriquecer registos com informações das operações
      const registosEnriquecidos = registos.map(r => {
        const opInfo = operacoesMap[r.operacao_id] || {};
        return {
          ...r,
          operacao_codigo: opInfo.codigo || r.operacao_codigo || '',
          operacao_nome: opInfo.nome || r.operacao_nome || 'N/A',
        };
      });
      
      // Calcular estatísticas do lote
      // NOTA: totalProduzido deve ser o MÁXIMO das operações, não a soma de todas
      // porque cada operação produz as mesmas peças (são operações sequenciais)
      const opsDoLote = lote.operacoes_operador || [];
      let totalProduzido = 0;
      if (opsDoLote.length > 0) {
        // Usar a quantidade máxima produzida entre todas as operações
        totalProduzido = Math.max(...opsDoLote.map(op => op.quantidade_produzida_todos || 0));
      } else {
        // Fallback: usar o máximo dos registos individuais
        totalProduzido = registosEnriquecidos.length > 0
          ? Math.max(...registosEnriquecidos.map(r => r.quantidade_produzida || 0))
          : 0;
      }
      
      const primeiroInicio = registosEnriquecidos.length > 0 
        ? new Date(Math.min(...registosEnriquecidos.map(r => new Date(r.inicio).getTime())))
        : null;
      const ultimoFim = registosEnriquecidos.filter(r => r.fim).length > 0
        ? new Date(Math.max(...registosEnriquecidos.filter(r => r.fim).map(r => new Date(r.fim).getTime())))
        : null;
      
      // Calcular anomalias totais do lote (soma de todas as operações)
      let totalDefeitos = 0;
      let totalArranjos = 0;
      (lote.operacoes_operador || []).forEach(op => {
        totalDefeitos += op.defeitos || 0;
        totalArranjos += op.arranjos || 0;
      });
      
      setLoteConsultaData({
        lote,
        of,
        registos: registosEnriquecidos,
        totalProduzido,
        primeiroInicio,
        ultimoFim,
        totalDefeitos,
        totalArranjos
      });
    } catch (error) {
      console.error('Erro ao consultar lote:', error);
      toast.error('Erro ao carregar detalhes do lote');
    } finally {
      setLoadingConsulta(false);
    }
  };

  // Atualizar contador de pausa
  useEffect(() => {
    if (!emPausa || !producaoAtiva) return;
    
    const interval = setInterval(() => {
      const pausas = producaoAtiva.registo?.pausas || [];
      const pausaAtual = pausas.find(p => p.inicio && !p.fim);
      if (pausaAtual) {
        const inicioPausa = new Date(pausaAtual.inicio);
        const agora = new Date();
        const tempoPausaCalc = Math.floor((agora - inicioPausa) / 1000);
        setTempoPausaAtual(tempoPausaCalc);
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [emPausa, producaoAtiva]);

  // Função para retomar trabalho
  const handleRetomarTrabalho = async () => {
    if (!producaoAtiva?.registo?.id) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/producao/retomar/${producaoAtiva.registo.id}`, {
        novo_sprint: false
      });
      
      setEmPausa(false);
      setTempoPausaAtual(0);
      setMotivoPausa('');
      toast.success('De volta ao trabalho!');
      
      // Navegar para a página de produção
      if (isSimulation) {
        safeNavigate(`/sim/operador/${simUser}/producao`);
      } else {
        navigate('/operador/producao');
      }
    } catch (error) {
      toast.error('Erro ao retomar');
    } finally {
      setLoading(false);
    }
  };

  // Handler para descongelar com confirmação se houver produção ativa
  const handleDescongelarComConfirmacao = (op) => {
    if (producaoAtiva) {
      // Há produção ativa — informar e navegar para produção
      toast.error('Abrimos a operação em curso. Por favor conclua (parcial ou total) para retomar a operação congelada.', { duration: 5000 });
      // Fechar o drawer de congeladas
      setShowCongeladasDrawer(false);
      // Navegar para a produção activa
      if (isSimulation) {
        safeNavigate(`/sim/operador/${simUser}/producao`);
      } else {
        navigate('/operador/producao');
      }
    } else {
      // Sem produção ativa — descongelar directamente
      navigateToDescongelar(op);
    }
  };

  const navigateToDescongelar = (op) => {
    if (isSimulation) {
      safeNavigate(`/sim/operador/${simUser}/producao?descongelar=${op.registo_id}`);
    } else {
      navigate(`/operador/producao?descongelar=${op.registo_id}`);
    }
  };

  // Combinar todas as OFs para pesquisa
  const todasOfs = [...(ofsData.novas || []), ...(ofsData.iniciadas || []), ...(ofsData.terminadas || [])];
  
  // Calcular OFs novas que realmente têm operações disponíveis (para usar na contagem da tab)
  const ofsNovasComOperacoes = useMemo(() => {
    const filtered = (ofsData.novas || []).filter(of => {
      const lotes = of.lotes || [];
      let temOperacoesDisponiveis = false;
      
      lotes.forEach(lote => {
        const opsLote = lote.operacoes_operador || [];
        opsLote.forEach(op => {
          if (op.status === 'pendente' || !op.status) {
            const qtdProduzida = op.quantidade_produzida_todos || op.quantidade_produzida || 0;
            const qtdTotal = op.quantidade_total || lote.quantidade || 0;
            const defeitos = op.defeitos || 0;
            const arranjos = op.arranjos || 0;
            const disponivel = qtdTotal - qtdProduzida - defeitos - arranjos;
            if (disponivel > 0) {
              temOperacoesDisponiveis = true;
            }
          }
        });
      });
      
      return temOperacoesDisponiveis;
    });
    
    // Ordenar conforme planeamento: urgentes primeiro, depois por prioridade, depois por ordem
    return filtered.sort((a, b) => {
      // Urgentes primeiro
      if (a.urgente && !b.urgente) return -1;
      if (!a.urgente && b.urgente) return 1;
      // Depois por prioridade (menor = mais importante)
      const priA = a.prioridade ?? 3;
      const priB = b.prioridade ?? 3;
      if (priA !== priB) return priA - priB;
      // Depois por ordem de planeamento
      const ordA = a.ordem_planeamento ?? 999;
      const ordB = b.ordem_planeamento ?? 999;
      return ordA - ordB;
    });
  }, [ofsData.novas]);
  
  // Filtrar OFs para pesquisa - só mostra se houver texto
  const filteredOfs = searchTerm.length >= 2 
    ? todasOfs.filter(of => 
        of.numero_of.toLowerCase().includes(searchTerm.toLowerCase()) ||
        of.artigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        of.cliente?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  const handleSelectOf = (of) => {
    // Novo fluxo: abrir TaskDetailScreen em vez do IniciarDialog directamente
    setSelectedOFDetail(of);
    setShowSearchDrawer(false);
  };

  // Selecionar OF com operação específica pré-selecionada (para tab Iniciadas)
  const handleSelectOfComOperacao = (of, opEmCurso) => {
    // Verificar se esta operação é a produção ativa
    if (producaoAtiva?.registo) {
      const reg = producaoAtiva.registo;
      if (
        reg.operacao_id === opEmCurso.operacao_id &&
        reg.lote_id === (opEmCurso.lote_id || opEmCurso.lote?.id) &&
        reg.of_id === of.id
      ) {
        // É a operação ativa — navegar para a produção em curso
        if (isSimulation) {
          safeNavigate(`/sim/operador/${simUser}/producao`);
        } else {
          navigate('/operador/producao');
        }
        return;
      }
    }
    // Caso contrário, abrir StartOperationScreen
    const loteId = opEmCurso?.lote?.id || opEmCurso?.lote_id;
    const lote = of.lotes?.find(l => l.id === loteId);
    setSelectedOpStart({ of, op: opEmCurso, lote: lote || opEmCurso?.lote || of.lotes?.[0] });
    setSelectedOFDetail(null); // Fechar TaskDetailScreen se aberto
  };;

  const handleSelectOfComLote = (of, loteId) => {
    // Buscar info do lote
    const lote = (of.lotes || []).find(l => l.id === loteId);
    const codigoLote = lote?.codigo_lote || lote?.numero_lote || 'N/A';
    
    // Verificar se o operador tem operações atribuídas nesta OF
    const operacoesAtribuidas = (of.operacoes || [])
      .filter(opOf => opOf.operadores_atribuidos?.includes(user.id))
      .map(opOf => {
        const opInfo = operacoes.find(o => o.id === opOf.operacao_id);
        return opInfo ? { ...opInfo, tempo_ajustado: opOf.tempo_ajustado } : null;
      })
      .filter(Boolean);
    
    // Se não há operações atribuídas a este operador, mostrar modal de erro destacado
    if (operacoesAtribuidas.length === 0) {
      setLoteNaoAtribuido({
        of_numero: of.numero_of,
        lote_codigo: codigoLote,
        artigo: of.artigo || of.modelo,
        cliente: of.cliente
      });
      setShowSemOperacoesModal(true);
      return;
    }
    
    setSelectedOperacao(null);
    
    // Obter operações associadas a este lote específico
    const opsDoLote = lote?.operacoes_operador || [];
    const opsIdsDoLote = opsDoLote.map(o => o.operacao_id);
    
    // Obter a categoria do lote
    const categoriaIdLote = lote?.categoria_id;
    
    // Filtrar operações que estão associadas a este lote
    // O backend já filtra por categoria (incluindo categorias especiais como PROTO)
    const opsFiltradas = operacoesAtribuidas.filter(op => {
      const estaNoLote = opsIdsDoLote.includes(op.id);
      return estaNoLote;
    });
    
    // Enriquecer com status
    const opsEnriquecidas = opsFiltradas.map(op => {
      const opNoLote = opsDoLote.find(o => o.operacao_id === op.id);
      
      // Verificar se tem registo congelado
      const temCongelada = operacoesCongeladas.some(c => 
        c.operacao_id === op.id && c.lote_id === loteId
      );
      
      let statusOp = 'pendente';
      let desabilitada = false;
      
      if (temCongelada) {
        statusOp = 'congelada';
      } else if (opNoLote) {
        if (opNoLote.status === 'completa' || opNoLote.status === 'concluido' || opNoLote.status === 'concluída') {
          statusOp = 'terminada';
          desabilitada = true;
        } else {
          const qtdProduzida = opNoLote.quantidade_produzida_todos || 0;
          const qtdTotal = opNoLote.quantidade_total || lote?.quantidade || 1;
          const defeitos = opNoLote.defeitos || 0;
          const arranjos = opNoLote.arranjos || 0;
          const disponivel = qtdTotal - qtdProduzida - defeitos - arranjos;
          
          if (disponivel <= 0) {
            statusOp = 'terminada';
            desabilitada = true;
          } else if (qtdProduzida > 0) {
            statusOp = 'parcial';
          }
        }
      }
      
      return {
        ...op,
        statusOperacao: statusOp,
        desabilitada,
        quantidade_produzida: opNoLote?.quantidade_produzida_todos || 0,
        quantidade_total: opNoLote?.quantidade_total || lote?.quantidade || 0
      };
    });
    
    // Ordenar: pendentes primeiro, depois parciais, congeladas, terminadas
    const opsOrdenadas = opsEnriquecidas.sort((a, b) => {
      const ordem = { 'pendente': 0, 'parcial': 1, 'congelada': 2, 'terminada': 3 };
      return (ordem[a.statusOperacao] || 0) - (ordem[b.statusOperacao] || 0);
    });
    
    setShowSearchDrawer(false);
    
    // Novo fluxo: abrir TaskDetailScreen em vez de IniciarDialog
    // Se só há uma operação disponível, abrir StartOperationScreen directamente
    const opsPendentes = opsOrdenadas.filter(op => !op.desabilitada);
    const loteObj = (of.lotes || []).find(l => l.id === loteId);
    
    if (opsPendentes.length === 1) {
      // Apenas uma operação — ir directamente para StartOperationScreen
      setSelectedOpStart({ of, op: opsPendentes[0], lote: loteObj || of.lotes?.[0] });
    } else {
      // Múltiplas operações — mostrar TaskDetailScreen com lote pré-selecionado
      setPreSelectedLoteId(loteId);
      setSelectedOFDetail(of);
    }
  };

  // Função de callback quando QR Code é lido
  const handleQrScan = async (result) => {
    if (result && result[0]?.rawValue) {
      const qrData = result[0].rawValue;
      if (process.env.NODE_ENV === 'development') console.log('QR Code lido:', qrData);
      
      // Verificar se é um URL de lote (formato: https://xxx/lote/{lote_id})
      const loteUrlMatch = qrData.match(/\/lote\/([a-f0-9-]+)/i);
      
      if (loteUrlMatch) {
        // É um QR Code de lote - extrair o lote_id
        const loteId = loteUrlMatch[1];
        if (process.env.NODE_ENV === 'development') console.log('Lote ID extraído do QR:', loteId);
        
        // Primeiro, procurar nas OFs já carregadas
        let ofComLote = todasOfs.find(of => 
          (of.lotes || []).some(lote => lote.id === loteId)
        );
        
        if (ofComLote) {
          const lote = ofComLote.lotes.find(l => l.id === loteId);
          setScanning(false);
          setShowQrDialog(false);
          handleSelectOfComLote(ofComLote, loteId);
          toast.success(`OF ${ofComLote.numero_of} - Lote ${lote?.codigo_lote || lote?.numero_lote} encontrado!`);
        } else {
          // Lote não encontrado localmente - buscar na API (pode ser de OF não atribuída ao operador)
          try {
            setQrError('');
            toast.info('A procurar lote...');
            const response = await axios.get(`${API}/lotes/${loteId}/of`);
            ofComLote = response.data;
            
            if (ofComLote) {
              const lote = (ofComLote.lotes || []).find(l => l.id === loteId);
              setScanning(false);
              setShowQrDialog(false);
              handleSelectOfComLote(ofComLote, loteId);
              toast.success(`OF ${ofComLote.numero_of} - Lote ${lote?.codigo_lote || lote?.numero_lote} encontrado!`);
            }
          } catch (error) {
            console.error('Erro ao buscar OF pelo lote:', error);
            if (error.response?.status === 404) {
              setQrError('Lote não encontrado ou a OF não está em produção.');
            } else {
              setQrError('Erro ao procurar o lote. Tente novamente.');
            }
          }
        }
      } else {
        // Formato antigo: procurar OF pelo ID ou número
        const ofEncontrada = todasOfs.find(of => 
          of.id === qrData || 
          of.numero_of.toLowerCase() === qrData.toLowerCase()
        );
        
        if (ofEncontrada) {
          setScanning(false);
          setShowQrDialog(false);
          handleSelectOf(ofEncontrada);
          toast.success(`OF ${ofEncontrada.numero_of} encontrada!`);
        } else {
          setQrError('OF não encontrada ou não disponível para produção');
        }
      }
    }
  };

  const handleQrError = (error) => {
    console.error('Erro QR Scanner:', error);
    setScanning(false);
    
    if (error?.name === 'NotAllowedError' || error?.message?.includes('Permission')) {
      setCameraPermission('denied');
      if (isIOS) {
        setQrError('Câmara bloqueada no iOS. Vá a Definições > Safari > Câmara e permita o acesso. Ou use a entrada manual abaixo.');
      } else {
        setQrError('Permissão de câmara negada. Por favor, permita o acesso à câmara nas definições do browser.');
      }
      setShowImageUpload(true);
    } else if (error?.name === 'NotFoundError' || error?.message?.includes('not found')) {
      setQrError('Nenhuma câmara encontrada no dispositivo. Use a entrada manual.');
      setShowImageUpload(true);
    } else if (error?.name === 'NotReadableError' || error?.message?.includes('in use')) {
      setQrError('Câmara em uso por outra aplicação. Feche outras apps e tente novamente.');
    } else if (error?.name === 'OverconstrainedError') {
      setQrError('Câmara não suporta a configuração solicitada. A tentar alternativa...');
      // Tentar com configurações mais simples
      retryWithSimpleConstraints();
    } else {
      setQrError('Erro ao aceder à câmara. Use a entrada manual ou upload de imagem abaixo.');
      setShowImageUpload(true);
    }
  };

  // Tentar com configurações mais simples — reseta estados para html5-qrcode reiniciar
  const retryWithSimpleConstraints = async () => {
    try {
      // Apenas verificar se há câmara disponível, sem pré-abrir stream
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('mediaDevices não disponível');
      }
      setCameraPermission('granted');
      setScannerReady(true);
      setScanning(true);
      setQrError('');
    } catch (err) {
      console.error('Retry failed:', err);
      setQrError('Câmara não disponível. Use a entrada manual.');
      setShowImageUpload(true);
    }
  };

  // Verificar permissão de câmara antes de abrir o scanner
  const checkCameraPermission = async () => {
    try {
      // Verificar se navigator.permissions está disponível
      if (navigator.permissions && navigator.permissions.query) {
        const result = await navigator.permissions.query({ name: 'camera' });
        setCameraPermission(result.state);
        return result.state;
      }
      // Fallback: tentar aceder diretamente
      return 'prompt';
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.log('Permissions API não disponível, tentando acesso direto');
      return 'prompt';
    }
  };

  // Solicitar acesso à câmara — agora delega à html5-qrcode para evitar
  // duplo getUserMedia que falha em iOS PWA standalone.
  const requestCameraAccess = async () => {
    try {
      setQrError('');
      if (process.env.NODE_ENV === 'development') console.log('[QR Scanner] A preparar câmara (html5-qrcode)...');
      
      // Verificar se mediaDevices está disponível
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Câmara não suportada neste dispositivo/browser.');
      }

      // Não fazemos getUserMedia aqui — html5-qrcode gere o stream.
      setCameraPermission('granted');
      setScannerReady(true);
      setScanning(true);
      return true;
    } catch (error) {
      console.error('[QR Scanner] Erro ao preparar câmara:', error);
      handleQrError(error);
      return false;
    }
  };

  const handleOpenQrDialog = async () => {
    setQrError('');
    setManualQrInput('');
    setScannerReady(false);
    setScanning(false);
    setShowImageUpload(false);
    setShowQrDialog(true);
    
    // Pequeno delay para garantir que o dialog está montado
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Verificar permissão de câmara
    const permission = await checkCameraPermission();
    if (process.env.NODE_ENV === 'development') console.log('[QR Scanner] Estado da permissão:', permission);
    
    if (permission === 'denied') {
      setQrError(isIOS 
        ? 'Câmara bloqueada. Vá a Definições > Safari > Câmara e permita. Ou use as alternativas abaixo.'
        : 'Permissão de câmara negada. Vá às definições do browser para permitir.'
      );
      setShowImageUpload(true);
      return;
    }
    
    // Deixar html5-qrcode gerir a câmara diretamente
    await requestCameraAccess();
  };

  const handleCloseQrDialog = () => {
    if (process.env.NODE_ENV === 'development') console.log('[QR Scanner] A fechar dialog');
    setScanning(false);
    setScannerReady(false);
    setShowQrDialog(false);
    setQrError('');
    setManualQrInput('');
    setShowImageUpload(false);
  };

  // Função para processar upload de imagem com QR Code
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      // Usar a biblioteca jsQR para decodificar a imagem
      const img = new Image();
      img.src = URL.createObjectURL(file);
      
      img.onload = async () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        
        // Tentar decodificar com jsQR (se disponível) ou processar como texto
        try {
          // Import dinâmico do jsQR
          const jsQR = (await import('jsqr')).default;
          const code = jsQR(imageData.data, imageData.width, imageData.height);
          
          if (code) {
            if (process.env.NODE_ENV === 'development') console.log('[QR Scanner] QR Code decodificado da imagem:', code.data);
            handleQrScan([{ rawValue: code.data }]);
            handleCloseQrDialog();
          } else {
            toast.error('Não foi possível ler o QR Code na imagem. Tente outra imagem ou use a entrada manual.');
          }
        } catch (jsQrError) {
          console.error('jsQR não disponível:', jsQrError);
          toast.error('Erro ao processar imagem. Use a entrada manual.');
        }
        
        URL.revokeObjectURL(img.src);
      };
    } catch (error) {
      console.error('Erro ao processar imagem:', error);
      toast.error('Erro ao processar imagem');
    }
  };

  // Função para input manual de QR Code
  const handleManualQrInput = async () => {
    if (!manualQrInput.trim()) {
      toast.error('Digite o número da OF ou cole o link do lote');
      return;
    }
    
    const qrData = manualQrInput.trim();
    
    // Verificar se é um URL de lote (formato: https://xxx/lote/{lote_id})
    const loteUrlMatch = qrData.match(/\/lote\/([a-f0-9-]+)/i);
    
    if (loteUrlMatch) {
      // É um URL de lote - extrair o lote_id
      const loteId = loteUrlMatch[1];
      
      // Primeiro, procurar nas OFs já carregadas
      let ofComLote = todasOfs.find(of => 
        (of.lotes || []).some(lote => lote.id === loteId)
      );
      
      if (ofComLote) {
        const lote = ofComLote.lotes.find(l => l.id === loteId);
        handleCloseQrDialog();
        handleSelectOfComLote(ofComLote, loteId);
        toast.success(`OF ${ofComLote.numero_of} - Lote ${lote?.codigo_lote || lote?.numero_lote} encontrado!`);
      } else {
        // Buscar na API
        try {
          setQrError('');
          toast.info('A procurar lote...');
          const response = await axios.get(`${API}/lotes/${loteId}/of`);
          ofComLote = response.data;
          
          if (ofComLote) {
            const lote = (ofComLote.lotes || []).find(l => l.id === loteId);
            handleCloseQrDialog();
            handleSelectOfComLote(ofComLote, loteId);
            toast.success(`OF ${ofComLote.numero_of} - Lote ${lote?.codigo_lote || lote?.numero_lote} encontrado!`);
          }
        } catch (error) {
          console.error('Erro ao buscar OF pelo lote:', error);
          if (error.response?.status === 404) {
            setQrError('Lote não encontrado ou a OF não está em produção.');
          } else {
            setQrError('Erro ao procurar o lote. Tente novamente.');
          }
        }
      }
    } else {
      // Formato antigo: procurar OF pelo ID ou número
      const ofEncontrada = todasOfs.find(of => 
        of.id === qrData || 
        of.numero_of.toLowerCase() === qrData.toLowerCase()
      );
      
      if (ofEncontrada) {
        handleCloseQrDialog();
        handleSelectOf(ofEncontrada);
        toast.success(`OF ${ofEncontrada.numero_of} encontrada!`);
      } else {
        setQrError('OF não encontrada ou não disponível para produção');
      }
    }
  };

  const handleLogout = () => {
    logout();
    if (isSimulation) {
      // Em modo simulação, voltar à página inicial (não ao login com teclado)
      navigate('/');
    } else {
      navigate('/operador/login');
    }
  };

  const formatDate = () => {
    return new Date().toLocaleDateString('pt-PT', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  // Loading enquanto carrega utilizador simulado
  if (isSimulation && !simulatedUser) {
    return (
      <div className="min-h-screen bg-warm-bg p-6">
        <div className="max-w-md mx-auto space-y-4">
          {/* Header placeholder */}
          <div className="flex items-center gap-3">
            <Skeleton className="h-14 w-14 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
          {/* Botões de acção placeholder */}
          <div className="grid grid-cols-2 gap-3 pt-4">
            {[0, 1, 2, 3].map(i => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Se não tem user, mostra loading
  if (!user) {
    return (
      <div className="min-h-screen bg-warm-bg p-6">
        <div className="max-w-md mx-auto space-y-4">
          <div className="flex items-center gap-3">
            <Skeleton className="h-14 w-14 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-4">
            {[0, 1, 2, 3].map(i => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-28" style={{ background: '#F8FAFC' }}>
      {/* Componente de Alertas */}
      {user?.id && <AlertasNotificacao userId={user.id} perfil="operador" />}

      {/* Banner flutuante de Tarefa Pontual Ativa — aparece em TODAS as tabs exceto no timer */}
      {tarefaPontualTimer && bottomTab !== 'tarefa_timer' && (
        <div
          className="fixed top-0 left-0 right-0 z-[60] px-3 pt-2 pb-1"
          style={{ background: 'rgba(248,250,252,0.95)', backdropFilter: 'blur(8px)' }}
        >
          <div
            className="flex items-center gap-3 px-4 py-3 rounded-2xl cursor-pointer shadow-lg transition-all active:scale-[0.98]"
            onClick={() => {
              // Se congelada, abrir o flow de descongelar em vez de mostrar o timer ativo
              if (tarefaPontualFrozen) {
                setShowDescongelarConfirm(tarefaPontualTimer);
              } else {
                setBottomTab('tarefa_timer');
              }
            }}
            data-testid="tarefa-pontual-floating-banner"
            style={{
              background: '#FFFFFF',
              border: tarefaPontualFrozen
                ? '2px solid #3B82F6'
                : (tarefaPontualPaused || pausaProgramadaAtiva) ? '2px solid #F59E0B' : '2px solid #7C3AED',
            }}
          >
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${(!tarefaPontualFrozen && (tarefaPontualPaused || pausaProgramadaAtiva)) ? 'animate-bounce' : ''}`}
              style={{ background: tarefaPontualFrozen ? '#DBEAFE' : (tarefaPontualPaused || pausaProgramadaAtiva) ? '#FEF3C7' : '#EDE9FE' }}
            >
              {tarefaPontualFrozen
                ? <Snowflake className="h-5 w-5" style={{ color: '#3B82F6' }} />
                : (tarefaPontualPaused || pausaProgramadaAtiva)
                  ? <Pause className="h-5 w-5" style={{ color: '#D97706' }} />
                  : <Play className="h-5 w-5" style={{ color: '#7C3AED' }} />
              }
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm truncate" style={{ color: '#1E293B' }}>
                {tarefaPontualFrozen
                  ? 'Tarefa Congelada'
                  : pausaProgramadaAtiva ? 'Pausa Programada' : tarefaPontualPaused ? 'Tarefa em Pausa' : 'Tarefa Pontual em curso'}
                <span className="ml-2 font-mono" style={{ color: tarefaPontualFrozen ? '#3B82F6' : (tarefaPontualPaused || pausaProgramadaAtiva) ? '#D97706' : '#7C3AED' }}
                  data-testid="floating-banner-tarefa-timer"
                >
                  {String(Math.floor(tarefaPontualTempo / 3600)).padStart(2,'0')}:{String(Math.floor((tarefaPontualTempo % 3600) / 60)).padStart(2,'0')}:{String(tarefaPontualTempo % 60).padStart(2,'0')}
                </span>
              </p>
              <p className="text-xs truncate" style={{ color: '#64748B' }}>
                {tarefaPontualFrozen ? 'Toque para retomar · ' : ''}{tarefaPontualTimer.nome || tarefaPontualTimer.titulo || 'Tarefa Pontual'}
              </p>
            </div>
            <ChevronRight className="h-5 w-5 flex-shrink-0" style={{ color: '#94A3B8' }} />
          </div>
        </div>
      )}

      {/* Ecrã de Operações (tab nav) — mostra por cima do conteúdo principal */}
      {bottomTab === 'operacoes' && !selectedOFDetail && !selectedOpStart && (
        <div className="fixed inset-0 z-[45]" style={{ background: '#F8FAFC' }}>
          <OperacoesScreen
            ofsNovas={ofsNovasComOperacoes}
            ofsIniciadas={ofsData.iniciadas}
            ofsTerminadas={ofsData.terminadas}
            operacoesCongeladas={operacoesCongeladas}
            producaoAtiva={producaoAtiva}
            onSelectOf={(of) => { setSelectedOFDetail(of); }}
            onScanQR={() => { setBottomTab('inicio'); setShowQrDialog(true); }}
            onResumeCongelada={(op) => handleDescongelarComConfirmacao(op)}
            tarefasPontuais={tarefasAtribuidas.filter(t => {
              // Excluir tarefa que o operador já está a executar
              if (tarefaPontualTimer && t.id === tarefaPontualTimer.id) return false;
              // Excluir tarefas que outro operador já iniciou
              if (t.iniciado_em && t.operador_id && t.operador_id !== user?.id) return false;
              return true;
            })}
            tarefasHistoricoDia={tarefasHistoricoDia}
            frozenTarefaId={tarefaPontualFrozen ? tarefaPontualTimer?.id : null}
            onOpenTarefas={() => {
              setOperacoesInitialTab('pontuais');
              setBottomTab('operacoes');
            }}
            onIniciarTarefa={(tarefa) => {
              abrirTarefa(tarefa);
            }}
            onEditarTarefaConcluida={(tarefa) => {
              setSelectedTarefa(tarefa);
              setTarefaConclusao({ quantidade: String(tarefa.quantidade_concluida || tarefa.quantidade_reportada || '') });
              setShowConcluirTarefaModal(true);
            }}
            onStartOperation={({ of, op, lote }) => {
              // Fluxo direto: abrir StartOperationScreen desde o menu Operações
              setSelectedOpStart({ of, op, lote });
            }}
            onOpenActiveProduction={() => {
              // Navegar para a página de produção ativa
              if (isSimulation) {
                safeNavigate(`/sim/operador/${simUser}/producao`);
              } else {
                navigate('/operador/producao');
              }
            }}
            initialTab={operacoesInitialTab}
          />
        </div>
      )}

      {/* Ecrã de Alertas/Notificações (tab nav) */}
      {bottomTab === 'alertas' && (
        <div className="fixed inset-0 z-[45]" style={{ background: '#F8FAFC' }}>
          <AlertasScreen userId={user?.id} />
        </div>
      )}

      {/* Ecrã de Perfil (tab nav) */}
      {bottomTab === 'perfil' && (
        <div className="fixed inset-0 z-[45]" style={{ background: '#F8FAFC' }}>
          <PerfilScreen
            userId={user?.id}
            username={user?.username}
            onLogout={() => {
              if (isSimulation) {
                safeNavigate('/sim');
              } else {
                setShowLogoutConfirm(true);
              }
            }}
            onFotoUpdated={(novaFotoUrl) => {
              setAvatarError(false);
              if (simulatedUser) {
                setSimulatedUser(prev => ({
                  ...prev,
                  avatar: novaFotoUrl.startsWith('http') ? novaFotoUrl : `${BACKEND_URL}${novaFotoUrl}`,
                  foto_url: novaFotoUrl,
                }));
              }
            }}
          />
        </div>
      )}


      {/* Timer de Tarefa Pontual */}
      {bottomTab === 'tarefa_timer' && tarefaPontualTimer && (
        <div className="fixed inset-0 z-[45]" style={{ background: '#F8FAFC' }}>
          <TimerScreen
            producaoData={null}
            lote={null}
            operacao={null}
            tarefaPontual={tarefaPontualTimer}
            tempoDecorrido={tarefaPontualTempo}
            isPaused={tarefaPontualPaused}
            isPontual={true}
            onPause={() => {
              setShowPausaPontualDrawer(true);
            }}
            onRetomar={() => setTarefaPontualPaused(false)}
            onConcluir={concluirTarefaPontualTimer}
            onCongelar={() => {
              // Garantir que só pode existir UMA congelada (op ou tarefa) em simultâneo
              if ((operacoesCongeladas || []).length > 0) {
                toast.error('Já tem uma operação congelada. Retome-a antes de congelar uma tarefa pontual.');
                return;
              }
              // Mostrar confirmação antes de congelar
              setShowCongelarConfirm(true);
            }}
            onBack={() => {
              // Navegar para trás sem parar o timer — manter tarefa a correr em segundo plano
              setBottomTab('operacoes');
            }}
            onChamarGestor={() => handleChamarGestor()}
            onNavigate={(tab) => setBottomTab(tab)}
            user={user}
          />
          <BottomTabBar
            activeTab="timer"
            onChange={(tab) => {
              if (tab === 'timer') return;
              setBottomTab(tab);
            }}
            alertasCount={alertasNaoLidosCount}
            emProducao={true}
          />
        </div>
      )}

      {/* StartOperationScreen — overlay quando uma operação é seleccionada */}
      {selectedOpStart && (
        <div className="fixed inset-0 z-50" style={{ background: '#F8FAFC' }}>
          <StartOperationScreen
            of={selectedOpStart.of}
            operacao={selectedOpStart.op}
            lote={selectedOpStart.lote}
            user={user}
            isSimulation={isSimulation}
            simUser={simUser}
            loading={loading}
            setLoading={setLoading}
            onBack={() => setSelectedOpStart(null)}
            onCongelar={() => { setSelectedOpStart(null); }}
            safeNavigate={safeNavigate}
          />
        </div>
      )}

      {/* TaskDetailScreen — overlay quando uma OF é clicada */}
      {selectedOFDetail && (
        <div className="fixed inset-0 z-50" style={{ background: '#F8FAFC' }}>
          <TaskDetailScreen
            of={selectedOFDetail}
            operacoes={operacoes || []}
            operacoesCongeladas={operacoesCongeladas}
            user={user}
            onBack={() => { setSelectedOFDetail(null); setPreSelectedLoteId(null); }}
            onSelectOperacao={(op, lote) => {
              handleSelectOfComOperacao(selectedOFDetail, op);
            }}
            onScanLote={() => { setSelectedOFDetail(null); setPreSelectedLoteId(null); }}
            modoTestes={modoTestes}
            preSelectedLoteId={preSelectedLoteId}
          />
        </div>
      )}

      {/* Indicador de modo simulação */}
      {isSimulation && (
        <div className="text-white text-center py-1 text-xs font-medium" style={{ background: '#3B82F6' }}>
          Modo Simulação: {user.nome}
        </div>
      )}
      
      {/* Header — verde escuro (estilo Samidel) */}
      <header
        className="sticky top-0 z-40 pb-4"
        style={{ background: '#FFFFFF', borderBottom: '1px solid #E2E8F0' }}
      >
        <div className="px-4 pt-4 max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-3">
            {/* Utilizador */}
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-black text-lg"
                style={{ background: '#0B3B30' }}
              >
                {user.avatar && !avatarError ? (
                  <img
                    src={user.avatar}
                    alt=""
                    className="w-full h-full rounded-full object-cover"
                    onError={() => setAvatarError(true)}
                  />
                ) : (
                  <span>{user.nome?.charAt(0) || user.username?.charAt(0) || '?'}</span>
                )}
              </div>
              <div>
                <p className="text-xs font-medium flex items-center gap-1" style={{ color: '#94A3B8' }}>
                  <GreetingIcon className="h-3.5 w-3.5" style={{ color: greetingColor }} />
                  {greeting.text}
                </p>
                <h1 className="text-base font-extrabold leading-tight" style={{ color: '#1E293B' }}>
                  {(() => {
                    const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
                    const nomeCompleto = user.nome_completo || user.nome || '';
                    let nomes = nomeCompleto.split(' ').filter(n => n);
                    if (!nomes.length) nomes = (user.username || '').split('.').filter(n => n);
                    nomes = nomes.map(cap);
                    return nomes.length >= 2 ? `${nomes[0]} ${nomes[nomes.length - 1]}` : nomes[0] || user.username || 'Operador';
                  })()}
                </h1>
              </div>
            </div>

            {/* Acções do header */}
            <div className="flex items-center gap-3">
              {/* Refresh — ao centro, mais acessível */}
              <button
                onClick={versionStatus === 'outdated' ? forceRefresh : () => window.location.reload()}
                className="h-10 w-10 rounded-full flex items-center justify-center transition-colors hover:bg-stone-100"
                data-testid="btn-refresh"
              >
                <RefreshCw className={`h-5 w-5 ${versionStatus === 'outdated' ? 'text-red-500 animate-spin' : ''}`} style={{ color: versionStatus === 'outdated' ? undefined : '#64748B' }} />
              </button>
              {/* Chamar Gestor — à direita */}
              <button
                onClick={handleChamarGestor}
                className="w-12 h-12 rounded-full flex items-center justify-center transition-colors"
                style={{ background: '#DC2626' }}
                data-testid="btn-chamar-gestor"
                title="Chamar Gestor"
              >
                <PhoneCall className="h-5 w-5" style={{ color: '#FFFFFF' }} />
              </button>

              {/* Sair */}
              {!isSimulation && (
                <button
                  onClick={() => setShowLogoutConfirm(true)}
                  className="px-4 py-2 rounded-full text-sm font-bold border-2 transition-colors hover:bg-red-50"
                  style={{ borderColor: '#EF4444', color: '#EF4444' }}
                  data-testid="logout-button"
                >
                  Sair
                </button>
              )}
            </div>
          </div>

          {/* Data e congeladas */}
          <div className="flex items-center justify-between">
            <p className="text-xs" style={{ color: '#94A3B8' }}>
              {new Date().toLocaleDateString('pt-PT', { weekday: 'long', day: 'numeric', month: 'long' }).replace(/^\w/, c => c.toUpperCase())}
            </p>
            {operacoesCongeladas.length > 0 && (
              <button
                onClick={() => setShowCongeladasDrawer(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-white"
                style={{ background: '#3B82F6' }}
                data-testid="congeladas-badge"
              >
                <Snowflake className="h-3.5 w-3.5" />
                <span className="max-w-[72px] truncate">
                  {operacoesCongeladas.length === 1 
                    ? (operacoesCongeladas[0].operacao_codigo || '').slice(0, 8) || '1 cong.'
                    : `${operacoesCongeladas.filter(c => !(producaoAtiva && producaoAtiva.registo?.id === c.registo_id)).length} cong.`
                  }
                </span>
              </button>
            )}
          </div>
        </div>
      </header>
      {/* Banner de versão desatualizada */}
      {versionStatus === 'outdated' && (
        <div 
          className="mx-4 mt-2 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center justify-between cursor-pointer hover:bg-red-100 transition-colors"
          onClick={forceRefresh}
          data-testid="version-outdated-banner"
        >
          <div className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-red-600 animate-spin" />
            <div>
              <p className="text-sm font-semibold text-red-700">Atualização disponível!</p>
              <p className="text-xs text-red-600">{APP_VERSION} → {serverVersion}</p>
            </div>
          </div>
          <span className="text-xs text-red-600 font-medium px-2 py-1 bg-red-100 rounded-full">
            Toque para atualizar
          </span>
        </div>
      )}

      {/* Content */}
      <div className="p-4 max-w-lg mx-auto space-y-3">

        {/* Botões de ação rápida: Categorias em destaque (só se tiver lotes novos) + Operações congeladas */}
        {(() => {
          // Filtrar categorias em destaque que tenham OFs com operações disponíveis
          const categoriasComLotesNovos = categoriasDestaque.filter(cat => {
            return ofsNovasComOperacoes.some(of => 
              (of.lotes || []).some(lote => 
                lote.categoria_codigo === cat.codigo
              )
            );
          });
          
          // Contar quantas OFs cada categoria tem
          const ofsCountByCategoria = {};
          categoriasDestaque.forEach(cat => {
            ofsCountByCategoria[cat.codigo] = ofsNovasComOperacoes.filter(of =>
              (of.lotes || []).some(lote => lote.categoria_codigo === cat.codigo)
            ).length;
          });
          
          // Filtrar operações congeladas que NÃO são a mesma da produção activa
          const congeladasFiltradas = operacoesCongeladas.filter(c => {
            if (!producaoAtiva) return true;
            const registo = producaoAtiva.registo;
            return !(c.operacao_id === registo?.operacao_id && 
                     c.lote_id === registo?.lote_id &&
                     c.of_id === registo?.of_id);
          });
          
          const temItens = categoriasComLotesNovos.length > 0 || congeladasFiltradas.length > 0 || tarefasAtribuidas.length > 0;
          
          if (!temItens) return null;
          
          return (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {/* Tarefas atribuídas (Bolsa de Tarefas) */}
              {tarefasAtribuidas.length > 0 && (
                <Button
                  onClick={() => {
                    setOperacoesInitialTab('pontuais');
                    setBottomTab('operacoes');
                  }}
                  className="flex-shrink-0 h-10 rounded-full bg-purple-500 hover:bg-purple-600 text-white shadow-md"
                  data-testid="btn-tarefas"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Tarefas ({tarefasAtribuidas.length})
                </Button>
              )}
              
              {/* Operações congeladas */}
              {congeladasFiltradas.length > 0 && (
                <Button
                  onClick={() => setShowCongeladasDrawer(true)}
                  className="flex-shrink-0 h-10 rounded-full bg-blue-500 hover:bg-blue-600 text-white shadow-md"
                  data-testid="btn-congeladas"
                >
                  <Snowflake className="h-4 w-4 mr-2" />
                  Congeladas ({congeladasFiltradas.length})
                </Button>
              )}
              
              {/* Categorias em destaque - só aparece se tiver OFs com operações disponíveis */}
              {categoriasComLotesNovos.map((cat) => {
                const ofsCount = ofsCountByCategoria[cat.codigo] || 0;
                return (
                  <Button
                    key={cat.id}
                    onClick={() => {
                      // Alternar filtro pela categoria
                      if (filtroCategoria === cat.codigo) {
                        setFiltroCategoria(null);
                      } else {
                        setFiltroCategoria(cat.codigo);
                        setActiveTab('novas');
                      }
                    }}
                    className={`flex-shrink-0 h-10 rounded-full shadow-md ${
                      filtroCategoria === cat.codigo 
                        ? 'bg-amber-600 hover:bg-amber-700 ring-2 ring-amber-300' 
                        : 'bg-amber-500 hover:bg-amber-600'
                    } text-white`}
                    data-testid={`btn-categoria-${cat.codigo}`}
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    {cat.nome} ({ofsCount})
                  </Button>
                );
              })}
            </div>
          );
        })()}

        {/* Banner de Produção Ativa — fundo branco com contorno colorido */}
        {producaoAtiva && (
          <Card 
            className="relative overflow-hidden rounded-2xl shadow-soft cursor-pointer hover:shadow-float transition-all"
            onClick={() => {
              if (isSimulation) {
                safeNavigate(`/sim/operador/${simUser}/producao`);
              } else {
                navigate('/operador/producao');
              }
            }}
            data-testid="producao-ativa-banner"
            style={{
              background: '#FFFFFF',
              border: emPausa ? '2px solid #F59E0B' : '2px solid #10B981',
            }}
          >
            <div className="relative p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center ${emPausa ? 'animate-bounce' : ''}`}
                    style={{ background: emPausa ? '#FEF3C7' : '#DCFCE7' }}
                  >
                    {emPausa
                      ? <Pause className="h-6 w-6" style={{ color: '#D97706' }} />
                      : <Play className="h-6 w-6" style={{ color: '#059669' }} />
                    }
                  </div>
                  <div>
                    <p className="font-bold text-lg" style={{ color: '#1E293B' }}>
                      {emPausa ? 'Em Pausa' : 'Produção em curso'}
                      <span
                        className="ml-2 font-mono text-base"
                        style={{ color: emPausa ? '#D97706' : '#059669' }}
                        data-testid={emPausa ? 'banner-timer-pausa' : 'banner-timer-ativo'}
                      >
                        {String(Math.floor(bannerTempo / 3600)).padStart(2,'0')}:{String(Math.floor((bannerTempo % 3600) / 60)).padStart(2,'0')}:{String(bannerTempo % 60).padStart(2,'0')}
                      </span>
                    </p>
                    {emPausa && motivoPausa && (
                      <p className="text-sm font-bold" style={{ color: '#D97706' }}>
                        Motivo: {motivoPausa}
                        {pausaHoraFim && (
                          <span className="ml-2 text-xs font-medium" style={{ color: '#92400E' }}>
                            · Retoma as {pausaHoraFim}
                          </span>
                        )}
                        {tempoPausaAtual > 0 && (
                          <span className="ml-2 font-mono text-xs" style={{ color: '#92400E' }}>
                            ({String(Math.floor(tempoPausaAtual / 3600)).padStart(2,'0')}:{String(Math.floor((tempoPausaAtual % 3600) / 60)).padStart(2,'0')}:{String(tempoPausaAtual % 60).padStart(2,'0')} em pausa)
                          </span>
                        )}
                      </p>
                    )}
                    <p className="text-sm font-medium" style={{ color: '#475569' }}>
                      {producaoAtiva.of?.numero_of} · {producaoAtiva.of?.cliente || '—'} · {producaoAtiva.of?.artigo || '—'}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>
                      {producaoAtiva.lote?.codigo_lote || '—'} · {producaoAtiva.operacao?.codigo} {producaoAtiva.operacao?.nome || ''}
                    </p>
                  </div>
                </div>
                <ChevronRight className="h-6 w-6" style={{ color: '#94A3B8' }} />
              </div>
            </div>
          </Card>
        )}

        {/* Tabs de OFs */}
        <OFTabs
          activeTab={activeTab}
          onChange={(tab) => { setActiveTab(tab); setFiltroCategoria(null); }}
          counts={{
            novas: ofsNovasComOperacoes.length,
            iniciadas: ofsData.iniciadas.length,
            terminadas: ofsData.terminadas.length,
          }}
        />

        {/* Main Action Card - Só mostra na tab Novas e SEM filtro de categoria */}
        {activeTab === 'novas' && !filtroCategoria && (
          <Card className="p-6 bg-white rounded-3xl shadow-soft border-0">
            <h2 className="text-xl font-heading font-bold text-navy mb-2">
              Pronta para começar?
            </h2>
            <p className="text-stone-500 text-sm mb-6">
              Pesquise uma ordem de fabrico ou digitalize o QR Code
            </p>

            <div className="space-y-3">
              <Button
                className="w-full h-14 rounded-full bg-navy text-white hover:bg-navy-hover text-base font-semibold shadow-soft active:scale-[0.98] transition-transform"
                onClick={() => setShowSearchDrawer(true)}
                data-testid="search-of-button"
              >
                <Search className="h-5 w-5 mr-2" />
                Pesquisar OF
              </Button>
              <Button
                variant="outline"
                className="w-full h-14 rounded-full border-2 border-sage/30 text-sage hover:bg-sage-soft text-base font-medium"
                onClick={handleOpenQrDialog}
                data-testid="qr-button"
              >
                <QrCode className="h-5 w-5 mr-2" />
                Ler QR Code
              </Button>
            </div>
          </Card>
        )}

        {/* Lista de OFs filtrada por tab */}
        {(() => {
          let ofsFiltradas = [];
          let tituloSecao = '';
          
          if (activeTab === 'novas') {
            ofsFiltradas = ofsData.novas;
            tituloSecao = 'OFs Novas';
          } else if (activeTab === 'iniciadas') {
            ofsFiltradas = ofsData.iniciadas;
            tituloSecao = 'OFs Em Curso';
          } else {
            ofsFiltradas = ofsData.terminadas;
            tituloSecao = 'OFs Terminadas';
          }
          
          // Aplicar filtro de categoria se definido
          if (filtroCategoria && activeTab === 'novas') {
            ofsFiltradas = ofsFiltradas.filter(of => {
              const lotes = of.lotes || [];
              return lotes.some(lote => lote.categoria_codigo === filtroCategoria);
            });
            const catNome = categoriasDestaque.find(c => c.codigo === filtroCategoria)?.nome || filtroCategoria;
            tituloSecao = `OFs ${catNome}`;
          }
          
          // Para tab "novas", filtrar OFs que têm operações realmente disponíveis (pendentes)
          if (activeTab === 'novas') {
            ofsFiltradas = ofsFiltradas.filter(of => {
              const lotes = of.lotes || [];
              let temOperacoesDisponiveis = false;
              
              lotes.forEach(lote => {
                const opsLote = lote.operacoes_operador || [];
                opsLote.forEach(op => {
                  // Verificar se a operação está pendente E tem quantidade disponível
                  if (op.status === 'pendente' || !op.status) {
                    const qtdProduzida = op.quantidade_produzida_todos || op.quantidade_produzida || 0;
                    const qtdTotal = op.quantidade_total || lote.quantidade || 0;
                    const defeitos = op.defeitos || 0;
                    const arranjos = op.arranjos || 0;
                    const disponivel = qtdTotal - qtdProduzida - defeitos - arranjos;
                    if (disponivel > 0) {
                      temOperacoesDisponiveis = true;
                    }
                  }
                });
              });
              
              return temOperacoesDisponiveis;
            });
          }
          
          return ofsFiltradas.length > 0 ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-stone-500 uppercase tracking-wider">
                  {tituloSecao} ({ofsFiltradas.length})
                </h3>
                {filtroCategoria && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFiltroCategoria(null)}
                    className="text-xs text-stone-500 hover:text-stone-700"
                  >
                    Limpar filtro
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {activeTab === 'iniciadas' ? (
                  // Para tab iniciadas, mostrar operações em curso E outras disponíveis
                  <>
                    {ofsFiltradas.flatMap((of) => {
                      const operacoesEmCurso = of.operacoes_em_curso || [];
                      
                      // Buscar também as outras operações disponíveis (pendentes e parciais)
                      const lotes = of.lotes || [];
                      const outrasOperacoes = [];
                      
                      lotes.forEach(lote => {
                        const opsLote = lote.operacoes_operador || [];
                        opsLote.forEach(op => {
                          // Não incluir operações que já estão em curso
                          const jaEmCurso = operacoesEmCurso.some(ec => 
                            ec.operacao_id === op.operacao_id && ec.lote_id === lote.id
                          );
                          if (!jaEmCurso && (op.status === 'pendente' || op.status === 'parcial' || !op.status)) {
                            outrasOperacoes.push({
                              ...op,
                              lote_id: lote.id,
                              lote_codigo: lote.codigo_lote || lote.codigo || `L${lote.numero_lote}`,
                              lote_quantidade: lote.quantidade
                            });
                          }
                        });
                      });
                      
                      if (operacoesEmCurso.length === 0 && outrasOperacoes.length === 0) {
                        // Se não há operações em curso nem outras, mostrar card simples
                        return [(
                          <div
                            key={of.id}
                            className="rounded-2xl overflow-hidden cursor-pointer transition-transform active:scale-[0.99] hover:shadow-lg"
                            onClick={() => handleSelectOf(of)}
                            data-testid={`of-card-${of.numero_of}`}
                            style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '2px solid #0B3B30' }}
                          >
                            <div className="px-4 py-3 flex items-center justify-between" style={{ background: '#0B3B30' }}>
                              <div>
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className="font-black text-base text-white">{of.numero_of}</span>
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold text-white" style={{ background: '#3B82F6' }}>
                                    Continuar
                                  </span>
                                  {of.urgente && <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold text-white" style={{ background: '#EF4444' }}>URGENTE</span>}
                                </div>
                                <p className="text-sm font-bold" style={{ color: 'rgba(255,255,255,0.8)' }}>{of.artigo}</p>
                              </div>
                              <ChevronRight className="h-5 w-5 text-white opacity-60" />
                            </div>
                          </div>
                        )];
                      }
                      
                      const cards = [];
                      
                      // Mostrar operações em curso (com destaque)
                      operacoesEmCurso.forEach((op, idx) => {
                        // Determinar o estado visual da operação
                        const statusLabel = op.status === 'congelada' ? 'Congelado'
                          : op.status === 'pausada' ? 'Pausado'
                          : op.status === 'parcial' || op.status === 'parcial_outros' ? 'Parcial'
                          : 'Em curso';
                        const statusColor = op.status === 'congelada' ? { bg: '#DBEAFE', text: '#1D4ED8' }
                          : op.status === 'pausada' ? { bg: '#FEF3C7', text: '#B45309' }
                          : op.status === 'parcial' || op.status === 'parcial_outros' ? { bg: '#FFEDD5', text: '#C2410C' }
                          : { bg: '#DCFCE7', text: '#15803D' };

                        cards.push(
                          <div
                            key={`${of.id}-${op.lote_id}-${op.operacao_id}`}
                            className="rounded-2xl overflow-hidden cursor-pointer transition-transform active:scale-[0.99] hover:shadow-lg bg-white"
                            onClick={() => handleSelectOfComOperacao(of, op)}
                            data-testid={`of-op-card-${of.numero_of}-${op.operacao_codigo}`}
                            style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1.5px solid #10B981' }}
                          >
                            <div className="px-4 py-2.5">
                              {/* Linha 1: OF + URGENTE + status (canto direito, maior) */}
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-black text-sm" style={{ color: '#1E293B' }}>{of.numero_of}</span>
                                  {of.urgente && <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold text-white" style={{ background: '#EF4444' }}>URGENTE</span>}
                                </div>
                                <span
                                  className="px-3 py-1 rounded-full text-xs font-extrabold flex-shrink-0"
                                  style={{ background: statusColor.bg, color: statusColor.text }}
                                  data-testid={`status-badge-${op.operacao_codigo}`}
                                >
                                  {statusLabel}
                                </span>
                              </div>
                              {/* Linha 2: Artigo · Cliente */}
                              <p className="text-xs font-medium mb-2" style={{ color: '#94A3B8' }}>{of.artigo} · {of.cliente}</p>
                              {/* Linha 3: Operação (código + nome juntos) + quantidade */}
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <p className="text-base font-extrabold truncate" style={{ color: '#1E293B' }}>
                                    {op.operacao_codigo} {op.operacao_nome}
                                    {op.is_contagem && (
                                      <span className="text-[10px] px-1.5 py-0.5 rounded font-bold ml-1.5" style={{ background: '#DBEAFE', color: '#1D4ED8' }}>CONTAGEM</span>
                                    )}
                                  </p>
                                  <div className="flex items-center gap-1.5 mt-1">
                                    <span className="text-[10px] font-bold uppercase text-stone-400">Lote</span>
                                    <span className="font-black text-xs text-white px-2 py-0.5 rounded-md" style={{ background: '#0B3B30' }}>{op.lote_codigo}</span>
                                  </div>
                                </div>
                                {op.is_contagem ? (
                                  <span className="font-bold text-sm flex items-center gap-1 flex-shrink-0 ml-2" style={{ color: '#3B82F6' }}>
                                    <Calculator className="h-4 w-4" />
                                    Contagem
                                  </span>
                                ) : (
                                  <div className="flex flex-col items-end flex-shrink-0 ml-2">
                                    {op.quantidade_em_falta > 0 ? (
                                      <span className="font-bold text-lg" style={{ color: '#0B3B30' }}>{op.quantidade_em_falta} <span className="text-xs font-normal text-stone-500">em falta</span></span>
                                    ) : (
                                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Concluída</span>
                                    )}
                                    {(op.quantidade_produzida_todos > 0 || op.total_anomalias > 0) && (
                                      <span className="text-[10px] text-stone-400 mt-0.5">
                                        {op.quantidade_produzida_todos > 0 && `${op.quantidade_produzida_todos} feitas`}
                                        {op.quantidade_produzida_todos > 0 && op.total_anomalias > 0 && ' · '}
                                        {op.total_anomalias > 0 && <span className="text-red-500">{op.total_anomalias} anom.</span>}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      });
                      
                      // Mostrar outras operações disponíveis (mais discreto, mas mais informativo)
                      if (outrasOperacoes.length > 0) {
                        cards.push(
                          <div
                            key={`${of.id}-outras`}
                            className="rounded-2xl overflow-hidden cursor-pointer transition-transform active:scale-[0.99] hover:shadow-lg"
                            onClick={() => setSelectedOFDetail(of)}
                            data-testid={`of-outras-${of.numero_of}`}
                            style={{ border: '2px dashed #0B3B30', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                          >
                            <div className="px-4 py-3 flex items-center justify-between" style={{ background: 'rgba(11,59,48,0.06)' }}>
                              <div className="flex items-center gap-2">
                                <span className="font-black text-sm" style={{ color: '#0B3B30' }}>{of.numero_of}</span>
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold text-white" style={{ background: '#3B82F6' }}>
                                  {outrasOperacoes.length} disponíveis
                                </span>
                              </div>
                              <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#64748B' }}>Não iniciadas</span>
                            </div>
                            <div className="bg-white px-4 py-3">
                              <div className="flex flex-wrap gap-1.5">
                                {outrasOperacoes.slice(0, 3).map((op, idx) => (
                                  <span key={`${op.operacao_id}-${idx}`}
                                    className="text-xs px-2 py-1 rounded-lg flex items-center gap-1 font-medium"
                                    style={op.is_contagem ? { background: '#DBEAFE', color: '#1D4ED8', border: '1px solid #BFDBFE' } : { background: '#F1F5F9', color: '#334155', border: '1px solid #E2E8F0' }}
                                  >
                                    {op.is_contagem && <Calculator className="h-3 w-3" />}
                                    {op.codigo} — {op.nome?.substring(0, 18)}{op.nome?.length > 18 ? '…' : ''}
                                  </span>
                                ))}
                                {outrasOperacoes.length > 3 && <span className="text-xs text-stone-400 py-1">+{outrasOperacoes.length - 3}</span>}
                              </div>
                            </div>
                          </div>
                        );
                      }
                      
                      return cards;
                    }).slice(0, 15)}
                  </>
                ) : activeTab === 'novas' ? (
                  // Para tab Novas, mostrar card RESUMO por OF
                  ofsFiltradas.slice(0, 10).map((of) => {
                    const lotes = of.lotes || [];
                    // Contar lotes com operações disponíveis
                    let lotesComOps = 0;
                    const operacoesUnicas = new Set();
                    
                    lotes.forEach(lote => {
                      const opsLote = lote.operacoes_operador || [];
                      let loteTemOps = false;
                      opsLote.forEach(op => {
                        if (op.status === 'pendente' || !op.status) {
                          const qtdProduzida = op.quantidade_produzida_todos || op.quantidade_produzida || 0;
                          const qtdTotal = op.quantidade_total || lote.quantidade || 0;
                          const defeitos = op.defeitos || 0;
                          const arranjos = op.arranjos || 0;
                          const disponivel = qtdTotal - qtdProduzida - defeitos - arranjos;
                          if (disponivel > 0) {
                            loteTemOps = true;
                            operacoesUnicas.add(op.operacao_id || op.codigo);
                          }
                        }
                      });
                      if (loteTemOps) lotesComOps++;
                    });
                    
                    const nOpsUnicas = operacoesUnicas.size;
                    
                    // Verificar se a OF tem lotes de categoria especial (ex: PROTO)
                    const hasEspecial = of.lotes?.some(l => {
                      const cat = (l.categoria_nome || l.categoria || '').toLowerCase();
                      return cat === 'proto' || cat === 'especial' || cat === 'amostra';
                    });
                    const categoriaNome = of.lotes?.find(l => l.categoria_nome)?.categoria_nome;
                    
                    return (
                      <OFCard
                        key={of.id}
                        of={of}
                        onClick={() => setSelectedOFDetail(of)}
                      />
                    );
                  })
                ) : (
                  // Para tab terminadas, mostrar normalmente
                  ofsFiltradas.slice(0, 10).map((of) => {
                    const progresso = of.progresso_operador || { total_operacoes: 0, completas: 0, percentagem: 0 };
                    
                    return (
                      <div
                        key={of.id}
                        className="rounded-2xl overflow-hidden cursor-pointer transition-transform active:scale-[0.99] hover:shadow-lg"
                        onClick={() => setSelectedOFDetail(of)}
                        data-testid={`of-card-${of.numero_of}`}
                        style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #E2E8F0' }}
                      >
                        {/* Header verde escuro */}
                        <div className="px-4 py-3 flex items-start justify-between" style={{ background: '#0B3B30' }}>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                              <span className="font-black text-base text-white">{of.numero_of}</span>
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold text-white" style={{ background: '#10B981' }}>Concluída</span>
                              {of.urgente && <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold text-white" style={{ background: '#EF4444' }}>URGENTE</span>}
                            </div>
                            <p className="text-sm font-bold" style={{ color: 'rgba(255,255,255,0.8)' }}>{of.cliente}</p>
                            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>{of.artigo}</p>
                          </div>
                          <div className="text-right flex flex-col items-end gap-1">
                            {progresso.total_operacoes > 0 && (
                              <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.9)' }}>
                                {progresso.percentagem}%
                              </span>
                            )}
                            <ChevronRight className="h-5 w-5" style={{ color: 'rgba(255,255,255,0.5)' }} />
                          </div>
                        </div>
                        {/* Corpo branco */}
                        <div className="bg-white px-4 py-3">
                          {progresso.total_operacoes > 0 && (
                            <div>
                              <div className="h-1.5 rounded-full overflow-hidden mb-1.5" style={{ background: '#E2E8F0' }}>
                                <div className="h-full rounded-full" style={{ width: `${progresso.percentagem}%`, background: '#10B981' }} />
                              </div>
                              <p className="text-xs font-bold" style={{ color: '#64748B' }}>{progresso.completas}/{progresso.total_operacoes} operações</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ) : (activeTab !== 'novas' || (!producaoAtiva && operacoesCongeladas.length === 0)) ? (
            <Card className="p-8 bg-white rounded-2xl shadow-soft border-0 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-stone-100 flex items-center justify-center">
                <Package className="h-8 w-8 text-stone-400" />
              </div>
              <p className="text-stone-500">
                {activeTab === 'novas' ? 'Nenhuma OF nova com lotes prontos' :
                 activeTab === 'iniciadas' ? 'Nenhuma OF em curso' :
                 'Nenhuma OF terminada'}
              </p>
            </Card>
          ) : null;
        })()}
        
        {/* Espaço para o badge "Made with Emergent" não obstruir */}
        <div className="h-16" />
      </div>

      {/* Drawer de Pesquisa */}
      <PesquisaDrawer
        open={showSearchDrawer}
        onOpenChange={setShowSearchDrawer}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filteredOfs={filteredOfs}
        user={user}
        onSelectOf={(of) => { handleSelectOf(of); setShowSearchDrawer(false); }}
      />
      {/* Dialog QR Code - Usando Componente */}
      <QrScannerDialog
        open={showQrDialog}
        onClose={handleCloseQrDialog}
        scanning={scanning}
        scannerReady={scannerReady}
        cameraPermission={cameraPermission}
        qrError={qrError}
        isIOS={isIOS}
        showImageUpload={showImageUpload}
        manualQrInput={manualQrInput}
        setManualQrInput={setManualQrInput}
        onScan={handleQrScan}
        onError={handleQrError}
        onManualSubmit={handleManualQrInput}
        onImageUpload={handleImageUpload}
        onRequestCameraAccess={requestCameraAccess}
      />


      {/* Dialog de Iniciar — LEGADO (removido, substituído por StartOperationScreen) */}

      {/* Botão flutuante de pausa REMOVIDO — informação de pausa já aparece no banner de produção */}


      {/* Drawer de Operações Congeladas */}
      <CongeladasDrawer
        open={showCongeladasDrawer}
        onOpenChange={setShowCongeladasDrawer}
        operacoesCongeladas={operacoesCongeladas.filter(c => {
          const prod = producaoAtiva;
          return !(prod && prod.registo?.id === c.registo_id);
        })}
        modoTestes={modoTestes}
        onDescongelar={(op) => {
          handleDescongelarComConfirmacao(op);
        }}
        onDescartar={async (registoId) => {
          try {
            await axios.delete(`${API}/producao/congelada/${registoId}`);
            setOperacoesCongeladas(prev => prev.filter(c => c.registo_id !== registoId));
            toast.success('Operação congelada descartada');
          } catch (e) {
            toast.error('Erro ao descartar operação');
          }
        }}
        producaoAtiva={producaoAtiva}
        producaoTempo={bannerTempo}
        onIrProducaoAtiva={() => {
          if (isSimulation) {
            navigate(`/sim/operador/${simUser}/producao`);
          } else {
            navigate('/operador/producao');
          }
        }}
        tarefaPontualTimer={tarefaPontualTimer}
        tarefaPontualTempo={tarefaPontualTempo}
        tarefaPontualPaused={tarefaPontualPaused}
        onIrTarefaPontual={() => {
          setBottomTab('tarefa_timer');
        }}
      />

      {/* Modal de Sem Operações Atribuídas - Alerta destacado */}
      {/* Modal de Sem Operações Atribuídas */}
      <SemOperacoesModal
        open={showSemOperacoesModal}
        onOpenChange={setShowSemOperacoesModal}
        loteInfo={loteNaoAtribuido}
        onClose={() => setLoteNaoAtribuido(null)}
      />
      
      {/* Dialog de Confirmação de Logout */}
      {/* Dialog de Confirmação de Logout */}
      <LogoutConfirmDialog
        open={showLogoutConfirm}
        onOpenChange={setShowLogoutConfirm}
        operacoesCongeladas={operacoesCongeladas}
        producaoAtiva={producaoAtiva}
        onLogout={handleLogout}
        onVerCongeladas={() => setShowCongeladasDrawer(true)}
      />

      {/* Drawer de Consulta de Lote */}
      <LoteConsultaDrawer
        open={showLoteConsulta}
        onOpenChange={(v) => { setShowLoteConsulta(v); if (!v) setLoteConsultaData(null); }}
        loteConsultaData={loteConsultaData}
        loadingConsulta={loadingConsulta}
      />
      {/* Drawer de Tarefas Pontuais */}
      <TarefasDrawer
        open={showTarefasDrawer}
        onOpenChange={setShowTarefasDrawer}
        tarefasAtribuidas={tarefasAtribuidas}
        tarefasHistoricoDia={tarefasHistoricoDia}
        tarefasTab={tarefasTab}
        onTabChange={setTarefasTab}
        onAbrirTarefa={abrirTarefa}
      />


      {/* Modal de Conclusão de Tarefa */}
      {/* Drawer de Pausa — Tarefa Pontual */}
      {/* Drawer de Pausa — Tarefa Pontual (mesmo estilo das operações normais) */}

      {/* Modal de Confirmação — Congelar Tarefa Pontual */}
      {showCongelarConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-sm bg-white rounded-3xl p-6" data-testid="modal-congelar-pontual">
            <div className="text-center mb-4">
              <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: '#EFF6FF' }}>
                <Snowflake className="h-7 w-7" style={{ color: '#3B82F6' }} />
              </div>
              <h3 className="text-lg font-black" style={{ color: '#1E293B' }}>Congelar tarefa?</h3>
              <p className="text-sm mt-1" style={{ color: '#64748B' }}>
                A tarefa fica congelada com o tempo decorrido preservado. Pode iniciar outra tarefa e retomar esta mais tarde.
              </p>
            </div>
            <div className="space-y-2">
              <button
                onClick={async () => {
                  if (tarefaTimerRef.current) clearInterval(tarefaTimerRef.current);
                  try {
                    const res = await axios.post(`${API}/tarefas/${tarefaPontualTimer.id}/congelar`);
                    setTarefaPontualTimer(res.data);
                    if (res.data.tempo_decorrido_segundos != null) {
                      setTarefaPontualTempo(res.data.tempo_decorrido_segundos);
                    }
                  } catch (e) {
                    console.error('Erro ao congelar tarefa:', e);
                  }
                  // Congelar é diferente de Pausar — não ativar tarefaPontualPaused
                  setTarefaPontualFrozen(true);
                  setShowCongelarConfirm(false);
                  toast.info('Tarefa pontual congelada');
                  setBottomTab('operacoes');
                }}
                className="w-full py-3.5 rounded-2xl font-bold text-white text-base"
                style={{ background: '#3B82F6' }}
                data-testid="btn-confirmar-congelar"
              >
                Sim, congelar
              </button>
              <button
                onClick={() => setShowCongelarConfirm(false)}
                className="w-full py-3 rounded-2xl font-semibold text-sm"
                style={{ color: '#64748B', background: '#F1F5F9' }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação — Descongelar/Retomar Tarefa Pontual */}
      {showDescongelarConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-sm bg-white rounded-3xl p-6" data-testid="modal-descongelar-pontual">
            <div className="text-center mb-4">
              <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: '#F0FDF4' }}>
                <Play className="h-7 w-7" style={{ color: '#16A34A' }} />
              </div>
              <h3 className="text-lg font-black" style={{ color: '#1E293B' }}>Retomar tarefa?</h3>
              <p className="text-sm mt-1" style={{ color: '#64748B' }}>
                A tarefa será retomada de onde parou.
              </p>
            </div>
            <div className="space-y-2">
              <button
                onClick={async () => {
                  // Retomar — chama backend para descongelar (limpa congelado_em, regista ultimo_descongelado_em)
                  try {
                    const res = await axios.post(`${API}/tarefas/${tarefaPontualTimer.id}/descongelar`);
                    setTarefaPontualTimer(res.data);
                    if (res.data.tempo_decorrido_segundos != null) {
                      setTarefaPontualTempo(res.data.tempo_decorrido_segundos);
                    }
                  } catch (e) {
                    console.error('Erro ao descongelar tarefa:', e);
                  }
                  setTarefaPontualPaused(false);
                  setTarefaPontualFrozen(false);
                  setShowDescongelarConfirm(null);
                  setBottomTab('tarefa_timer');
                  toast.success('Tarefa retomada');
                }}
                className="w-full py-3.5 rounded-2xl font-bold text-white text-base"
                style={{ background: '#16A34A' }}
                data-testid="btn-confirmar-descongelar"
              >
                Sim, retomar
              </button>
              <button
                onClick={() => setShowDescongelarConfirm(null)}
                className="w-full py-3 rounded-2xl font-semibold text-sm"
                style={{ color: '#64748B', background: '#F1F5F9' }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <Drawer open={showPausaPontualDrawer} onOpenChange={setShowPausaPontualDrawer}>
        <DrawerContent className="flex flex-col" style={{ borderRadius: '24px 24px 0 0', overflow: 'hidden', maxHeight: '80vh', marginBottom: '70px' }}>
          {/* Header verde escuro */}
          <div style={{ background: '#0B3B30' }} className="px-5 py-4 flex-shrink-0">
            <DrawerHeader className="p-0 text-left">
              <DrawerTitle className="text-xl font-black text-white">Fazer Pausa</DrawerTitle>
              <DrawerDescription className="text-white/60 text-sm font-medium mt-0.5">
                Selecione o motivo da pausa
              </DrawerDescription>
            </DrawerHeader>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {MOTIVOS_PAUSA_PONTUAL.map((motivo) => {
              const isSelected = motivoPausaPontual === motivo.id;
              return (
                <button
                  key={motivo.id}
                  onClick={() => setMotivoPausaPontual(motivo.id)}
                  className="w-full flex items-center gap-3 py-4 px-5 rounded-xl text-left font-semibold transition-all active:scale-98"
                  style={{
                    background: isSelected ? '#FEF3C7' : '#F8FAFC',
                    border: isSelected ? '2px solid #F59E0B' : '1px solid #E2E8F0',
                    color: '#1E293B',
                  }}
                  data-testid={`btn-pausa-${motivo.id}`}
                >
                  <span className="text-xl">{motivo.icon}</span>
                  <span className="text-sm">{motivo.label}</span>
                </button>
              );
            })}
          </div>
          {/* Botão confirmar */}
          <div className="px-4 pb-4 pt-2 flex-shrink-0">
            <button
              onClick={() => {
                setTarefaPontualPaused(true);
                if (tarefaTimerRef.current) clearInterval(tarefaTimerRef.current);
                setShowPausaPontualDrawer(false);
                const motivo = MOTIVOS_PAUSA_PONTUAL.find(m => m.id === motivoPausaPontual);
                toast.info(`Pausa: ${motivo?.label || 'Sem motivo'}`);
              }}
              disabled={!motivoPausaPontual}
              className="w-full py-4 rounded-2xl font-bold text-white text-base transition-opacity active:opacity-80 disabled:opacity-40"
              style={{ background: '#0B3B30' }}
              data-testid="btn-confirmar-pausa-pontual"
            >
              Confirmar Pausa
            </button>
          </div>
        </DrawerContent>
      </Drawer>

      <ConcluirTarefaModal
        open={showConcluirTarefaModal}
        onOpenChange={setShowConcluirTarefaModal}
        tarefa={selectedTarefa}
        conclusao={tarefaConclusao}
        onConclusaoChange={setTarefaConclusao}
        onConfirmar={concluirTarefa}
      />

      {/* Barra de navegação inferior */}
      <BottomTabBar
        activeTab={bottomTab}
        onChange={(tab) => {
          // Fechar overlays abertos (TaskDetailScreen, StartOperationScreen)
          setSelectedOFDetail(null);
          setSelectedOpStart(null);
          
          if (tab === 'timer') {
            // Abrir drawer de produção (mostra ativa + pontuais + congeladas)
            setShowCongeladasDrawer(true);
            return;
          }
          setBottomTab(tab);
        }}
        alertasCount={alertasNaoLidosCount}
        emProducao={!!producaoAtiva}
      />
    </div>
  );
}
